class CustomProductAccessibility {
  static ensureUniqueId(input, fallback = 'custom-field') {
    const baseIdentifier = input.id || input.name || fallback;
    let identifier = baseIdentifier;
    let index = 1;
    if (!input.id || document.getElementById(input.id) !== input) {
      identifier = `${baseIdentifier}-${index}`;
      while (document.getElementById(identifier)) {
        index += 1;
        identifier = `${baseIdentifier}-${index}`;
      }
      input.id = identifier;
    }
    return identifier;
  }

  static setupDatePicker(input, picker, calendarId) {
    const calendar = picker.calendarContainer;
    if (!calendar) return { syncNavigation() {}, destroy() {} };

    const handlers = [];
    const enhanceMonthControl = (control, label) => {
      if (!control) return;
      control.setAttribute('role', 'button');
      control.setAttribute('aria-label', label);
      const handleKeydown = (event) => {
        if (!['Enter', ' '].includes(event.key) || control.getAttribute('aria-disabled') === 'true') return;
        event.preventDefault();
        event.stopPropagation();
        control.click();
      };
      control.addEventListener('keydown', handleKeydown);
      handlers.push({ control, handleKeydown });
    };
    const syncNavigation = () => {
      const minDate = picker.config.minDate;
      const maxDate = picker.config.maxDate;
      const previousDisabled = Boolean(minDate) && (
        picker.currentYear < minDate.getFullYear()
        || (picker.currentYear === minDate.getFullYear() && picker.currentMonth <= minDate.getMonth())
      );
      const nextDisabled = Boolean(maxDate) && (
        picker.currentYear > maxDate.getFullYear()
        || (picker.currentYear === maxDate.getFullYear() && picker.currentMonth >= maxDate.getMonth())
      );
      for (const [control, disabled] of [
        [picker.prevMonthNav, previousDisabled],
        [picker.nextMonthNav, nextDisabled]
      ]) {
        if (!control) continue;
        control.tabIndex = disabled ? -1 : 0;
        control.setAttribute('aria-disabled', String(disabled));
      }
    };
    this.setExpanded(input, false);
    calendar.id = calendarId;
    calendar.classList.add('custom-field-date-calendar');
    calendar.setAttribute('role', 'dialog');
    calendar.setAttribute('aria-label', `${input.getAttribute('aria-label')} calendar`);
    input.setAttribute('aria-controls', calendarId);
    enhanceMonthControl(picker.prevMonthNav, 'Previous month');
    enhanceMonthControl(picker.nextMonthNav, 'Next month');
    syncNavigation();

    return {
      syncNavigation,
      destroy() {
        handlers.forEach(({ control, handleKeydown }) => {
          control.removeEventListener('keydown', handleKeydown);
        });
      }
    };
  }

  static setFieldError(field, input, error, text) {
    if (!error) return;
    if (!error.id) {
      error.id = `${input?.id || input?.name || 'custom-field'}-error`;
    }
    error.setAttribute('role', 'alert');
    error.setAttribute('aria-live', 'polite');
    error.innerHTML = text;
    if (!input) return;
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', error.id);
  }

  static clearFieldError(input, error) {
    error?.remove();
    if (!input) return;
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedby');
  }

  static setExpanded(element, expanded) {
    element?.setAttribute('aria-expanded', String(expanded));
  }

  static setPressed(element, pressed) {
    element?.setAttribute('aria-pressed', String(pressed));
  }

  static syncSelectedOptions(options, selectedClass) {
    options.forEach(option => {
      option.setAttribute('aria-selected', String(option.classList.contains(selectedClass)));
    });
  }

  static setActiveDescendant(input, option) {
    if (!input || !option) return;
    input.setAttribute('aria-activedescendant', option.id);
  }

  static clearActiveDescendant(input) {
    input?.removeAttribute('aria-activedescendant');
  }

  static getFocusableElements(root) {
    if (!root) return [];
    return root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
  }

  static setFocusable(root, enabled) {
    this.getFocusableElements(root).forEach((element) => {
      if (enabled) {
        if (element.dataset.previousTabindex) {
          element.setAttribute('tabindex', element.dataset.previousTabindex);
          delete element.dataset.previousTabindex;
        } else {
          element.removeAttribute('tabindex');
        }
        return;
      }
      if (element.hasAttribute('tabindex')) {
        element.dataset.previousTabindex = element.getAttribute('tabindex');
      }
      element.setAttribute('tabindex', '-1');
    });
  }

  static prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

class CustomProductParent extends HTMLElement {
  constructor() {
    super();
    this.productCache = new Map();
    this.productInfoRequestId = 0;
    this.bundleProductRequestId = 0;
    this.abortController = null;
    this.assembledFields = [];
    this.attrImagePreviews = {
      timer: null,
      currentNode: null,
      clickController: null,
      width: 220,
      isHoverDevice: window.matchMedia('(hover: hover) and (pointer: fine)').matches
    }
    this._wrapper = this.closest('.custom-product-template');
    this.exchangeRate = this.parseExchangeRate(FoxTheme.ctm?.customExchangeRate);
  }
  get defaultOptions() {
    const textContent = this._wrapper.querySelector(`script[type="application/json"][data-selected-variant]`)?.textContent;
    return textContent ? JSON.parse(textContent) : {};
  }
  get skeleton() {
    return this._wrapper.querySelector('.custom-field-skeleton');
  }
  get skuImage() {
    return this._wrapper.querySelectorAll('.image-swatch[data-preview-image]');
  }
  get selectField() {
    return this.querySelector('.custom-select-field');
  }
  get quantityInput() {
    return this.querySelector('custom-quantity-input');
  }
  get addToCartForm() {
    return this._wrapper.querySelector('.add-to-cart-form');
  }
  get addToCartButton() {
    return this.addToCartForm?.querySelector('button.custom-add-to-cart');
  }
  get slides() {
    return this._wrapper.querySelector('custom-main-template-slides')
  }
  get selectedKeys() {
    return this.assembledFields.filter(item => !item.hide).reduce((pre, cur) => {
      return pre.concat(cur.values?.filter((value) => value.selected))
    }, []).filter((item) => item?.key);
  }
  get addToCartFormRect() {
    return this.addToCartForm.getBoundingClientRect();
  }
  encrypt(num, times = 100) {
    const base = Math.round(num * times) + 622152;
    return base * 10 + 7;
  }
  handleize(name) {
    if (!name || typeof name !== 'string') {
      return '';
    }
    return name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  }
  formatCartDateValue(value) {
    const rawValue = `${value || ''}`.trim();
    const parts = rawValue.split('/');
    if (parts.length !== 3) return rawValue;
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  priceFromCents(cents) {
    const str = String(Math.round(cents)).padStart(3, '0');
    return str.slice(0, -2) + '.' + str.slice(-2);
  }
  parseExchangeRate(value) {
    const rate = Number.parseFloat(value);
    return Number.isFinite(rate) && rate > 0 ? rate : 1.5;
  }
  convertAddonPrice(price) {
    return Math.round((price || 0) * this.exchangeRate);
  }
  normalizeEncodedAddonPrice(price) {
    return Math.trunc(Math.round((price || 0) * 10) / 10);
  }
  checkEdgePrice(list, value) {
    if (list.type === 'range') {
      return value >= list.min && value <= list.max;
    }
    if (list.type === 'exact') {
      return value === list.value;
    }
    return false;
  }
  computedPrice(priceRule = {}) {
    if (priceRule.ruleType === 'fixed') {
      return Math.trunc(priceRule.amount * 100);
    } else if (priceRule.ruleType === 'percentage') {
      const ratePerMille = Math.trunc(priceRule.rate * 1000);
      return this.defaultOptions.price * ratePerMille / 1000;
    }
    return 0;
  }
  computedEdgePrice(lists, type, value) {
    const list = lists.find((list) => list.edgeType === type && this.checkEdgePrice(list, value));
    if (!list) return 0;
    if (list.type == 'range') {
      const { min, stepSize, stepStart, stepPrice, price } = list;
      let totalPrice = 0;
      const isValidStepStart = Number.isFinite(stepStart) && stepStart >= 0;
      if (isValidStepStart) {
        totalPrice = stepStart * 100;
        let step = Math.ceil((value - min) / stepSize);
        totalPrice += step * stepPrice * 100;
      } else {
        totalPrice = price * 100;
      }
      return totalPrice;
    }
    if (list.type == 'exact') {
      return list.price * 100;
    }
    return 0;
  }
  computedSizePrice(option = {}) {
      const { pricingRule = {}, tps_width_inches, tps_length_inches } = option;
      if (!tps_width_inches || !tps_length_inches) return 0;
      const { dimension, tiers = [] } = pricingRule;
      const width = tps_width_inches * 1;
      const length = tps_length_inches * 1;
      const area = width * length;
      if (dimension === "edge") {
        const fieldWidthPrice = this.computedEdgePrice(tiers, 'width', width);
        const fieldLengthPrice = this.computedEdgePrice(tiers, 'length', length);
        return fieldWidthPrice + fieldLengthPrice;
      }
      if (dimension === "area") {
        return this.computedEdgePrice(tiers, '', area);
      }
      return 0;
  }
  createSizeTitle(option) {
    const { tps_width_value, tps_length_value, tps_width_fraction, tps_length_fraction, tps_size_unit } = option;
    if (!tps_width_value || !tps_length_value) return '';
    let width_fraction = '';
    let length_fraction = '';
    if (tps_width_fraction && !['-/-','0/0'].includes(tps_width_fraction)) {
      width_fraction = ` ${tps_width_fraction}`;
    }
    if (tps_length_fraction && !['-/-','0/0'].includes(tps_length_fraction)) {
      length_fraction = ` ${tps_length_fraction}`;
    }
    let width = `${tps_width_value}${width_fraction}"W`;
    let length = `${tps_length_value}${length_fraction}"L`;
    if (tps_size_unit === 'cm') {
      width = `${tps_width_value}cm W`;
      length = `${tps_length_value}cm L`;
    }
    return `${width} x ${length}`;
  }
  computedNumberPrice(option = {}) {
      const { pricingRule = {}, current_value } = option;
      if (!current_value) return 0;
      const { tiers = [] } = pricingRule;
      const value = current_value * 1;
      return this.computedEdgePrice(tiers, '', value);
  }
  bindClickClose() {
    if ('AbortController' in window) {
      this.attrImagePreviews.clickController?.abort();
      this.attrImagePreviews.clickController = new AbortController();
      document.addEventListener('click', this.onClickOutside.bind(this), { signal: this.attrImagePreviews.clickController.signal });
    } else {
      document.addEventListener('click', this.onClickOutside.bind(this));
    }
  }
  onClickOutside(e) {
    const wrapper = this.attrImagePreviews.currentNode.closest('.image-swatch-wrapper');
    if (e.target.closest('.attr-image-preview') || wrapper.contains(e.target)) {
      return;
    }
    this.removePreviewImage();
    this.unbindClickOutside();
  }
  unbindClickOutside() {
    if (this.attrImagePreviews.clickController) {
      this.attrImagePreviews.clickController.abort();
      this.attrImagePreviews.clickController = null;
    } else {
      document.removeEventListener('click', this.onClickOutside);
    }
  }
  removePreviewImage() {
    const previewNode = document.querySelector('.attr-image-preview');
    previewNode?.remove();
    clearTimeout(this.attrImagePreviews.timer);
  }
  createPreviewImage(image) {
    const { width } = this.attrImagePreviews;
    const { left, right, top, bottom, width: imageWidth } = image.getBoundingClientRect();
    const innerWidth = window.innerWidth;
    const widthCenter = left + imageWidth / 2;
    const rightSpace = (widthCenter + width / 2) < innerWidth;
    const leftSpace = widthCenter > width / 2;
    const topSpace = top - width > 88;
    let topPosition = topSpace ? top - width - 22 : bottom + 16;
    const leftPosition = leftSpace ? (rightSpace ? widthCenter - width / 2 : right - width) : left;
    this.removePreviewImage();
    const field = image.closest('fieldset');
    let image_title = '';
    if (field && field.dataset.type == 'Color') {
      const title = image.closest('label.image-swatch-wrapper');
      image_title = title.getAttribute('title');
      topPosition = topSpace ? topPosition - 16 : topPosition - 8;
    }
    const node = `<div style="--width: ${width}px;--left: ${leftPosition}px;--top: ${topPosition}px;" class="attr-image-preview ${image_title ? 'attr-image-preview--color': ''} ${topSpace ? 'attr-image-preview--top' : 'attr-image-preview--bottom'} ${rightSpace ? '' : 'attr-image-preview--right'} ${leftSpace ? '' : 'attr-image-preview--left'}">
      <img width="100%" height="100%" src="${image.dataset.previewImage}">
      ${image_title ? `<div class="attr-image-preview__content">${image_title}</div>` : ''}
    </div>`;
    document.body.insertAdjacentHTML('beforeend', node);
    if (!this.attrImagePreviews.isHoverDevice) {
      this.attrImagePreviews.currentNode = image;
      window.addEventListener('scroll', () => {
        this.removePreviewImage();
      }, { passive: true, once: true });
      this.bindClickClose();
      clearTimeout(this.attrImagePreviews.timer);
      this.attrImagePreviews.timer = setTimeout(() => {
        this.removePreviewImage();
      }, 5000)
    }
  }
  handlePreviewImage(imageLabel, image) {
    if (this.attrImagePreviews.isHoverDevice) {
      let timer = null;
      imageLabel.addEventListener('mouseenter', () => {
        timer = setTimeout(() => {
          this.createPreviewImage(image);
        }, 200);
      });
      imageLabel.addEventListener('mouseleave', () => {
        this.removePreviewImage();
        clearTimeout(timer);
      });
    } else {
      imageLabel.addEventListener('click', () => {
        this.createPreviewImage(image);
      })
    }
  }

  attrImagePreview() {
    if (!this.skuImage.length) return;
    for (const image of this.skuImage) {
      const previewImage = image.dataset.previewImage;
      if (!previewImage) continue;
      // Accessibility - Screen Reading
      const imageLabel = image.closest('label');
      if (!imageLabel) return;
      this.handlePreviewImage(imageLabel, image)
    }
  }
  updateExpandButton() {
    if (!this.showExpandButton) return;
    this.showExpandButton.classList.toggle('color-show', Boolean(this.showExpandButtonStatus));
  }
  expandButtonStatusHandle() {
    if (!this.showExpandButton) return;
    if (this.expandButtonStatus) {
      this.colorFieldImages.classList.add('expanded');
    } else {
      this.colorFieldImages.classList.remove('expanded');
    }
    this.showExpandButton.innerHTML = this.expandButtonStatus ? 'Collapse Colors' : 'Expand Colors';
    CustomProductAccessibility.setExpanded(this.showExpandButton, this.expandButtonStatus);
  }
  colorImageToggle() {
    this.colorFields = this._wrapper.querySelector('fieldset[data-type="Color"]');
    this.colorFieldImages = this.colorFields?.querySelector('custom-field-sku-image');
    this.showExpandButton = this.colorFields?.querySelector('.custom-field-color-toggle');
    if (!this.colorFieldImages) return;
    if (window.innerWidth > 768) return;
    const scrollWidth = this.colorFieldImages.scrollWidth;
    const clientWidth = this.colorFieldImages.clientWidth;
    this.expandButtonStatus = false;
    this.showExpandButton?.addEventListener('click', () => {
      this.expandButtonStatus = !this.expandButtonStatus;
      this.expandButtonStatusHandle();
    });
    if (scrollWidth > clientWidth) {
      this.showExpandButtonStatus = true;
      this.updateExpandButton();
    }
  }
  initOptions() {
    const { options } = this.defaultOptions;
    this.assembledFields = this.fields.map((field, _index) => {
      const { optionKey, optionType, source, validationRules, values } = field;
      const _values = (values || []).map((value) => {
        const { valueKey, displayValue, isDefault } = value
        const selected = source == 1 ? options.includes(displayValue) : isDefault;
        return {
          ...value,
          key: valueKey,
          label: displayValue,
          optionKey,
          source,
          selected
        }
      });
      if (['text', 'number', 'date'].includes(optionType)) {
        field.current_value = validationRules?.defaultValue;
      }
      return {
        ...field,
        key: optionKey,
        values: _values,
        fieldIndex: _index
      };
    });
    for (const field of this.assembledFields) {
      this.fieldMap.set(field.key, field);
      if (field.conditionalLogic) { this.ruleMap.set(field.key, field.conditionalLogic); }
      for (const value of field.values) {
        this.fieldMap.set(value.key, value);
        if (value.conditionalLogic) { this.ruleMap.set(value.key, value.conditionalLogic); }
      }
    }
  }
  customFieldsData() {
    const fieldScript = this._wrapper.querySelector('script[type="application/json"][data-custom-fields]');
    this.fields = fieldScript?.textContent ? JSON.parse(fieldScript.textContent) : [];
    fieldScript?.remove();
    this.assembledFields = [];
    this.fieldMap = new Map();
    this.ruleMap = new Map();
    this._totalPrice = 0;
    this.initOptions();
  }
  initWebComponents() {
    this.updateField();
    this.attrImagePreview();
    this.colorImageToggle();
    this.handlePropertiesSubmit();
    this.updateAddToCartButtonState();
  }
  initFieldEvent() {
    this.addEventListener("update-field-input", (e) => {
      const key = e.target.closest('fieldset').dataset.key;
      const value = e.target.value;
      this.updateField(key, { current_value: value });
    });
    this.addEventListener("update-field-checked", (e) => {
      const key = e.target.closest('fieldset').dataset.key;
      const value = e.target.value;
      this.updateField(key, value);
    });
    this.addEventListener("update-field-size", (e) => {
      const {
        widthInputValue,
        lengthInputValue,
        widthFractionSelectValue,
        lengthFractionSelectValue,
        widthInchesValue,
        lengthInchesValue,
        sizeUnit
      } = e.target;
      const key = e.target.closest('fieldset').dataset.key;
      this.updateField(key, {
        tps_width_value: widthInputValue,
        tps_width_fraction: widthFractionSelectValue,
        tps_length_value: lengthInputValue,
        tps_length_fraction: lengthFractionSelectValue,
        tps_width_inches: widthInchesValue,
        tps_length_inches: lengthInchesValue,
        tps_size_unit: sizeUnit
      });
    });
  }
  connectedCallback() {
    this.initFieldEvent();
    this.observeAddToCart();
    this.handleAccordionEvent();
  }
  handleAccordionEvent() {
    this.handleAccordionPosition();
    document.addEventListener('matchMobile', this.handleAccordionPosition.bind(this));
    document.addEventListener('unmatchMobile', this.handleAccordionPosition.bind(this));
  }
  handleAccordionPosition() {
    const isMobile = FoxTheme.config.mqlMobile;
    const pcBlocks = this._wrapper.querySelectorAll('.product__accordion__pc [data-block-id]');

    pcBlocks.forEach((pcBlock) => {
      const blockId = pcBlock.dataset.blockId;
      const mobileBlock = this._wrapper.querySelector(`.product__accordion__mobile [data-block-id="${blockId}"]`);
      if (!mobileBlock) return;
      if (isMobile) {
        if (!pcBlock.hasAttribute('data-moved-to-mobile')) {
          mobileBlock.innerHTML = pcBlock.innerHTML;
          pcBlock.innerHTML = '';
          pcBlock.setAttribute('data-moved-to-mobile', '');
        }
      } else if (pcBlock.hasAttribute('data-moved-to-mobile')) {
        pcBlock.innerHTML = mobileBlock.innerHTML;
        mobileBlock.innerHTML = '';
        pcBlock.removeAttribute('data-moved-to-mobile');
      }
    });
  }
  updateAddToCartButtonState(initializing = false) {
    const button = this.addToCartButton;
    if (!button) return;
    if (initializing) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('custom-loading-shine-btn');
      return;
    }
    button.disabled = button.dataset.canAddToCart !== 'true';
    button.setAttribute('aria-busy', 'false');
    button.classList.remove('custom-loading-shine-btn');
  }
  changeLoadingState(state = 'add') {
    if (state === 'remove') {
      this.addToCartButton?.classList.remove('custom-loading-btn');
      return;
    }
    this.addToCartButton?.classList.add('custom-loading-btn');
  }
  updateOptionValues(html) {
    const variantSelects = html.querySelector('custom-variant-picker');
    const variantSelectors = this._wrapper.querySelector('custom-variant-picker');
    if (variantSelects) {
      Morph.morph(variantSelectors, variantSelects);
      this.updateExpandButton();
      this.expandButtonStatusHandle();
    }
  }
  updateAddToCartButton(html) {
    const addToCart = html.querySelector('.product-form__buttons');
    const _addToCart = this._wrapper.querySelector('.product-form__buttons');
    if (addToCart) {
      Morph.morph(_addToCart, addToCart);
      this.updateAddToCartButtonState();
    }
  }
  updateColorImage(target) {
    const type = target.closest('fieldset').dataset.type;

    if (type !== 'Color') return;
    const showMultipleImage = this.slides.dataset.showMultipleImage;
    if (showMultipleImage === 'multiple') {
      const color_name = this.handleize(target.value);
      const showSlide = this.slides.querySelector(`custom-main-template-slide[data-key="${color_name}"]`);
      if (!showSlide) return;
      const currentSlide = this.slides.querySelector('custom-main-template-slide.active');
      currentSlide.classList.remove('active');
      showSlide.classList.add('active');
    }

    if (showMultipleImage === 'single') {
      const featured_media = this.defaultOptions.featured_media;
      if (!featured_media) return;
      const nextSlide = this.slides.querySelector(`.swiper-slide[data-key="${featured_media.id}"]`);
      if (!nextSlide) return;
      const index = nextSlide.dataset.index;
      const currentSlide = this.slides.querySelector('custom-main-template-slide.active');
      currentSlide.slideTo(index);
    }
  }
  updateOptionByKey(key, update) {
    const item = this.fieldMap.get(key);
    if (!item) return;
    const { optionType, values = [] } = item;
    if (['text', 'number', 'date', 'size'].includes(optionType)) {
      Object.assign(item, update);
      return;
    }
    const _values = values.map((value) => {
      value.selected = update.some((item) => item.key === value.key) ? true : false;
      return value;
    });
    Object.assign(item, { values: _values });
  }
  isSelected(key) {
    let dependItem = this.fieldMap.get(key);
    if (dependItem?.selected) {
      return !this.returnRuleChain(key);
    }
    return false;
  }
  returnRuleChain(key) {
    let item = this.fieldMap.get(key);
    if (!item) return false;
    if (!item.conditionalLogic) {
      if (key !== item.optionKey) {
        item = this.fieldMap.get(item.optionKey);
        if (!item) return false;
        if (!item.conditionalLogic) return false;
      } else { return false; }
    }
    const { conditions = [], relation } = item.conditionalLogic;
    const results = conditions.map((condition) => {
      const { comparator, valueKey } = condition;
      const isSelected = this.isSelected(valueKey);
      if (comparator === 'equals') {
        return isSelected;
      } else if (comparator === 'not_equals') {
        return !isSelected;
      }
      return false;
    });
    if (relation === 'and') {
      return results.every((result) => result);
    } else if (relation === 'or') {
      return results.some((result) => result);
    }
    return false;
  }
  updateOptionShowOrHide() {
    for (const option of this.assembledFields) {
      const { conditionalLogic, values = [], key } = option;
      for (const value of values) {
        if (!value.conditionalLogic) continue;
        const match = this.returnRuleChain(value.key);
        value[value.conditionalLogic.effect] = match;
      }
      if (!conditionalLogic) continue;
      const match = this.returnRuleChain(key);
      option[conditionalLogic.effect] = match;
    }
  }
  updateTotalPrice() {
    if (!this.defaultOptions.id) return;
    const initialPrice = this.defaultOptions.price * 1;
    let totalPrice = initialPrice;
    let sizePrice = 0;
    let numberPrice = 0;
    let percentagePrice = 0;
    let compareAtPrice = this.defaultOptions.compare_at_price ? this.defaultOptions.compare_at_price * 1 : 0;
    for (const option of this.assembledFields) {
      if (option.source === 1) continue;
      if (option.hide) continue;
      if (option.optionType === 'size') {
        sizePrice += this.computedSizePrice(option);
        continue;
      }
      if (option.optionType === 'number' && option.pricingRule) {
        numberPrice += this.computedNumberPrice(option);
        continue;
      }
      for(const value of option.values) {
        if (value.hide) continue;
        if (value.disable) continue;
        if(value.selected) {
          const addonPrice = this.computedPrice(value.pricingRule);
          if (value.pricingRule?.ruleType === 'percentage') {
            percentagePrice += addonPrice;
          } else {
            totalPrice += this.convertAddonPrice(addonPrice);
          }
        }
      }
    }
    totalPrice += this.convertAddonPrice(this.normalizeEncodedAddonPrice(sizePrice));
    totalPrice += this.convertAddonPrice(this.normalizeEncodedAddonPrice(numberPrice));
    totalPrice += this.convertAddonPrice(Math.round(percentagePrice));
    const priceElements = this._wrapper.querySelectorAll('.ctm-protemp_price');
    for (const priceElement of priceElements) {
      const totalPriceHtml = FoxTheme.Currency.formatMoney(this.priceFromCents(totalPrice), FoxTheme.settings.moneyFormat);
      if (compareAtPrice > initialPrice) {
        const savePrice = compareAtPrice - initialPrice;
        const compareAtPriceHtml = FoxTheme.Currency.formatMoney(this.priceFromCents(totalPrice + savePrice), FoxTheme.settings.moneyFormat);
        const savePriceHtml = FoxTheme.Currency.formatMoney(this.priceFromCents(savePrice), FoxTheme.settings.moneyFormat);
        priceElement.innerHTML = `<span class="ctm-protemp_sale">${totalPriceHtml}</span><span class="ctm-protemp_cpr">${compareAtPriceHtml}</span>`
      } else {
        priceElement.innerHTML = `<span class="ctm-protemp_so">${totalPriceHtml}</span>`;
      }
      this._totalPrice = totalPrice;
    }
  }
  updateCustomValue() {
     for (const option of this.assembledFields) {
      if (option.hide) {
        option.custom_value = '';
        continue;
      }
      if (option.optionType === 'size') {
        option.custom_value = this.createSizeTitle(option);
        continue;
      }
      if (option.optionType === 'date') {
        option.custom_value = option.current_value || '';
        continue;
      }
      if (option.optionType === 'text' && option.current_value) {
        option.custom_value = option.current_value;
        continue;
      }
      if (option.optionType === 'number' && option.current_value) {
        const displayUnit = option?.config?.displayUnit;
        option.custom_value = `${option.current_value}${displayUnit ? displayUnit : ''}`;
        continue;
      }
      const selectedValues = option.values.filter((value) => value.selected && !value.hide && !value.disable).map((value) => value.label);
      option.custom_value = selectedValues.join(',');
    }
  }
  updateCustomOption(option) {
    const field = this._wrapper.querySelector(`[data-key="${option.key}"]`);
    if (!field) return;
    field.setAttribute('aria-hidden', (option.hide || false));
    field.setAttribute('aria-disabled', (option.disable || false));
    if (field.matches('input, button, select, textarea')) {
      field.disabled = (option.disable || false);
    }
    for (const _value of (option.values || [])) {
      this.updateCustomOption(_value);
    }
  }
  updateCustomOptionTitle(option) {
    const { custom_value, key } = option;
    const field_title = this._wrapper.querySelector(`fieldset[data-key="${key}"] legend span.legend-value`);
    if (!field_title) return;
    if (field_title.innerHTML === custom_value) return;
    field_title.innerHTML = custom_value || '';
  }
  updateCustomOptionSort(option) {
    const { key, sortIndex } = option;
    const field_sort = this._wrapper.querySelector(`fieldset[data-key="${key}"] legend span.legend-sort`);
    if (!field_sort) return;
    const index = sortIndex + 1;
    if (field_sort.innerHTML === index) return;
    field_sort.innerHTML = index;
  }
  updateCustomOptionElement() {
    let offset = 0;
    for (const option of this.assembledFields) {
      if (option.hide) { offset = offset - 1; }
      option.sortIndex = option.fieldIndex + offset;
      if (option.source === 1) continue;
      this.updateCustomOption(option);
      this.updateCustomOptionTitle(option);
      this.updateCustomOptionSort(option);
    }
  }
  updateSelectFields() {
    if(!this.selectField) return;
    let selectHtml = '';
    for (const option of this.assembledFields) {
      selectHtml = selectHtml + (option.custom_value ? `<li class="custom-select-field_item">
        <span>${option.label}</span>
        <span>${option.custom_value}</span>
      </li>` : '');
    }
    this.selectField.innerHTML = selectHtml;
  }
  updateField(key, update) {
    this.updateOptionByKey(key, update);
    this.updateOptionShowOrHide();
    this.updateTotalPrice();
    this.updateCustomValue();
    this.updateCustomOptionElement();
    this.updateSelectFields();
  }
  fetchProductInfo(requestUrl) {
    if (!this.productCache.has(requestUrl)) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const promise = fetch(requestUrl, {
        signal: this.abortController.signal
      }).then((response) => response.text())
      .catch((error) => {
        this.productCache.delete(requestUrl);
        throw error;
      });
      this.productCache.set(requestUrl, promise);
    }
    return this.productCache
      .get(requestUrl)
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
  }
  renderProductInfo({ requestUrl, target }) {
    const requestId = ++this.productInfoRequestId;
    this.updateAddToCartButtonState(true);
    return this.fetchProductInfo(requestUrl)
      .then((html) => {
        if (requestId !== this.productInfoRequestId) return;
        this.updateOptionValues(html);
        this.updateAddToCartButton(html);
        this.updateField(target.dataset.optionKey, [{
          key: target.dataset.key
        }]);
        this.updateColorImage(target);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }
  validateFieldRule() {
    const validates = this.assembledFields.map((option) => {
      if (option.source === 1) return true;
      if (option.optionType === 'html') return true;
      if (option.hide) return true;
      const { optionType, key } = option;
      const fieldset = this._wrapper.querySelector(`fieldset[data-key="${key}"]`);
      if (optionType === 'text') {
        const fieldText = fieldset.querySelector('custom-field-text');
        const result = fieldText.validateRule();
        return result.ok; 
      }
      if (optionType === 'date') {
        const fieldDate = fieldset.querySelector('custom-field-date');
        const result = fieldDate.validateRule();
        return result.ok;
      }
      if (optionType === 'number') {
        const fieldNumber = fieldset.querySelector('custom-field-number');
        const result = fieldNumber.validateRule();
        return result.ok;
      }
      if (optionType === 'size') {
        const fieldSize = fieldset.querySelector('custom-field-size');
        let  resultWidth = { ok: true };
        let  resultLength = { ok: true };
        let  resultFractionWidth = { ok: true };
        let  resultFractionLength = { ok: true };
        resultWidth = fieldSize.validateRule('width');
        resultLength = fieldSize.validateRule('length');
        resultFractionWidth = fieldSize.fractionValidateRule('width');
        resultFractionLength = fieldSize.fractionValidateRule('length');
        return resultWidth.ok && resultLength.ok && resultFractionWidth.ok && resultFractionLength.ok;
      }
      if (optionType === 'dropdown') {
        const fieldSelect = fieldset.querySelector('custom-field-select');
        const result = fieldSelect.validateRule();
        return result.ok;
      }
      if (optionType === 'image_swatch') {
        const fieldImage = fieldset.querySelector('custom-field-image');
        const result = fieldImage.validateRule();
        return result.ok;
      }
      if (optionType === 'button') {
        const fieldButton = fieldset.querySelector('custom-field-button');
        const result = fieldButton.validateRule();
        return result.ok;
      }
      if (optionType === 'file_upload') {
        const fieldFile = fieldset.querySelector('custom-field-upload');
        const result = fieldFile.validateRule();
        return result.ok;
      }
      return true;
    })
    return validates.every((validate) => validate);
  }
  handlePropertiesSubmit() {
    this.addToCartForm?.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!this.validateFieldRule()) return;
      const customField = this.assembledFields.filter((option) => option.source === 0);
      let id = this.defaultOptions?.id;
      if (!id) { id = this.addToCartForm.querySelector('input[name="id"]')?.value; }
      const quantity = this.quantityInput?.value || 1;
      const formData = new FormData();
      formData.append('id', id);
      formData.append('quantity', quantity);
      const descriptions = customField.filter((f) => !f.hide && f.orderNote).map((f) => f.orderNote);
      if (descriptions?.length) {
        const description = descriptions.join(' -- ');
        formData.append('properties[_description]', description);
      }
      if (this._wrapper.tagName === 'CUSTOM-PRODUCT-BUNDLED') {
        const bundledTitle = this._wrapper.querySelector('.custom-product-bundled__title');
        const bundledValue = bundledTitle.querySelector('.custom-product-bundled__value');
        const title = bundledTitle.dataset.name;
        const value = bundledValue.innerText;
        if (title && value) {
          formData.append(`properties[${title}]`, value);
          formData.append(`properties[_custom_type]`, 'bundled');
        }
      }
      for (const field of customField) {
        if (field.optionType === 'html') continue;
        if (field.hide) continue;
        const label = field.orderLabel || field.label;
        const value = field.optionType === 'date'
          ? this.formatCartDateValue(field.custom_value)
          : field.custom_value;
        if (field.optionType === 'size') {
          const { tps_width_value, tps_length_value, tps_width_fraction ,tps_length_fraction, tps_width_inches, tps_length_inches, tps_size_unit } = field;
          let width = `${tps_width_value}"`;
          let _width = `${tps_width_inches}"`;
          if (tps_width_fraction && !['-/-','0/0'].includes(tps_width_fraction)) {
            width = `${tps_width_value} ${tps_width_fraction}"`;
            _width = `${tps_width_inches} ${tps_width_fraction}"`
          }
          let length = `${tps_length_value}"`;
          let _length = `${tps_length_inches}"`;
          if (tps_length_fraction && !['-/-','0/0'].includes(tps_length_fraction)) {
            length = `${tps_length_value} ${tps_length_fraction}"`;
            _length = `${tps_length_inches} ${tps_length_fraction}"`;
          }
          if (tps_size_unit === 'cm') {
            formData.append(`properties[_inches_size_width]`, _width);
            formData.append(`properties[_inches_size_length]`, _length);
            width = `${tps_width_value}cm`;
            length = `${tps_length_value}cm`;
          }
          formData.append(`properties[Width]`, width);
          formData.append(`properties[Length]`, length);
        } else if (field.optionType === 'file_upload') {
          const file = this.querySelector(`fieldset[data-key="${field.key}"] input[type="file"]`);
          if (!file.files[0]) continue;
          formData.append(`properties[${label}]`, file.files[0]);
        } else {
          if (!value) continue;
          formData.append(`properties[${label}]`, value);
        }
      }
      const selectedKeys = this.selectedKeys.filter(s => s.source === 0);
      let _tps = selectedKeys.map(s => s.key).join(',');
      const assembledFieldSize = this.assembledFields.filter((field) => field.optionType === 'size' && !field.hide);
      if (assembledFieldSize && assembledFieldSize.length > 0) {
        const sizePrice = assembledFieldSize.reduce((p, c) => {
          return p + this.computedSizePrice(c)
        }, 0);
        _tps += `${_tps ? ',' : ''}size:${this.encrypt(sizePrice / 100, 1000)}`;
      }
      const assembledNumber = this.assembledFields.filter((field) => field.optionType === 'number' && field.pricingRule && !field.hide);
      if (assembledNumber && assembledNumber.length > 0) {
        const numberPrice = assembledNumber.reduce((p, c) => {
          return p + this.computedNumberPrice(c);
        }, 0);
        _tps += `${_tps ? ',' : ''}number:${this.encrypt(numberPrice / 100, 1000)}`;
      }
      if (_tps) {
        _tps += `,tps:${this.encrypt(this._totalPrice / 100)}`;
        formData.append('properties[_tps]', _tps);
      }
      const sku_price = this.defaultOptions.price;
      if (sku_price >= 0) {
        formData.append('properties[_sku_price]', sku_price);
      }
      const pid = this._wrapper.dataset.pid;
      if (pid) {
        formData.append('properties[_pid]', pid);
      }
      formData.append('sections', 'cart-items-num');
      // console.log('this.assembledFields', this.assembledFields);
      // for (const [key, value] of formData) {
      //   console.log('====formData====', key, value);
      // }
      this.changeLoadingState()
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      }).then(response => {
        if (!response.ok) {
          throw new Error('Add to cart failed');
        }
        return response.json();
      }).then((cartData) => {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: true }
        }));
        const header = document.querySelector('ctm-header-container');
        const sections = cartData?.sections;
        if (header && sections) {
          header.updateItemNum(sections);
        }
      }).finally(() => {
        this.changeLoadingState('remove');
      })
    });
  }
  updateBtnStatus() {
    const { top, height } = this.addToCartFormRect;
    const { innerHeight } = window;
    const srcollUp = top - (this.prevTop || 0) > 0;
    if (top <= 0) {
      this.addToCartForm.classList.add('add-to-cart-form__sticky');
    } else if (top > height && top < innerHeight - height) {
      this.addToCartForm.classList.remove('add-to-cart-form__sticky');
      this.isExposed = true;
    } else if (srcollUp && this.isExposed) {
      this.addToCartForm.classList.add('add-to-cart-form__sticky');
    } else {
      this.addToCartForm.classList.remove('add-to-cart-form__sticky');
    }
    this.prevTop = top;
  }
  observeAddToCart() {
    if (window.innerWidth > 768) return;
    this.updateBtnStatus();
    window.addEventListener('scroll', () => {
      if(this.ticking) return;
      requestAnimationFrame(() => {
        this.updateBtnStatus();
        this.ticking = false;
      });
      this.ticking = true;
    }, { passive: true });
  }
}

if (!customElements.get('custom-product-template')) {
  class CustomProductTemplate extends CustomProductParent {
    constructor() {
      super();
    }
    updateUrl() {
      const { id } = this.defaultOptions;
      if (!id) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState(null, '', url);
    }
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener("variant-change", (e) => {
        this.renderProductInfo(e.detail).then(() => {
          this.updateUrl();
        })
      });
      requestAnimationFrame(() => {
        this.skeleton?.remove();
        this.customFieldsData();
        this.initWebComponents();
      })
    }
  }
  customElements.define('custom-product-template', CustomProductTemplate);
}
class CustomProductBundled extends CustomProductParent {
  constructor() {
    super();
    this.abortController = null;
  }
  get bundle_handle() {
    let handle = new URLSearchParams(window.location.search).get('bundle');
    if (!handle) {
      const handle_items = this.querySelector('.custom-product-bundled-items');
      if (!handle_items) return;
      handle = handle_items.querySelector('.active').dataset.handle;
    }
    return handle;
  }
  get bundle_sku() {
    return new URLSearchParams(window.location.search).get('sku')
  }
  fetchProduct({ handle, sku }) {
    const url = `/products/${handle}?view=bundled${sku ? `&variant=${sku}` : ''}`;
    if (!this.productCache.has(url)) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const promise = fetch(url, {
        signal: this.abortController.signal
      })
      .then(res => res.text())
      .catch(err => {
        this.productCache.delete(url);
        throw err;
      })
      this.productCache.set(url, promise);
    }
    return this.productCache
      .get(url)
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
  }
  updatePid(variantPickerDoc) {
    const pid = variantPickerDoc.dataset.pid;
    if (!pid) return;
    this.setAttribute('data-pid', pid);
  }
  updateUrlSku() {
    const url = new URL(window.location.href);
    const { id } = this.defaultOptions;
    if (!id) return;
    url.searchParams.set('sku', id);
    window.history.replaceState(null, '', url);
  }
  updateUrlHandle(handle) {
    if (!handle) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('sku');
    url.searchParams.set('bundle', handle);
    window.history.replaceState(null, '', url);
  }
  updateBundledValue(target) {
    const name = target.dataset.name;
    const _wrapper = target.closest('.custom-product-bundled__content');
    const bundledValue = _wrapper.querySelector('.custom-product-bundled__value');
    if (!bundledValue) return;
    bundledValue.innerHTML = name;
  }
  updateVariantPickerLoading(isLoading) {
    const variantPicker = this.querySelector('.custom-bundled__content custom-variant-picker');
    if (!variantPicker) return;
    variantPicker.classList.toggle('is-loading', isLoading);
    variantPicker.toggleAttribute('inert', isLoading);
    variantPicker.setAttribute('aria-busy', String(isLoading));
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', (e) => {
      const item = e.target.closest('.custom-product-bundled-item');
      if (!item || item.classList.contains('active')) return;
      this.querySelector('.custom-product-bundled-item.active')?.classList.remove('active');
      item.classList.add('active');
      const handle = item.dataset.handle;
      if (!handle) return;
      this.updateUrlHandle(handle);
      this.updateBundledValue(item);
      this.loadProduct(handle);
    });
    this.addEventListener("variant-change", (e) => {
      this.renderProductInfo(e.detail).then(() => {
        this.updateUrlSku();
      })
    });
    this.initLoadProduct({
      handle: this.bundle_handle,
      sku: this.bundle_sku
    });
  }
  updateMedia(mediaDoc) {
    const slides = this.querySelector('custom-main-template-slides');
    if (!slides) return;
    slides.replaceWith(mediaDoc.cloneNode(true));
  }
  updatePrice(priceDoc) {
    const prices = this.querySelectorAll('.ctm-protemp_price');
    if (!prices.length) return;
    for (const price of prices) {
      price.replaceWith(priceDoc.cloneNode(true));
    }
  }
  updateVariant(variantPickerDoc) {
    const bundledContent = this.querySelector('.custom-bundled__content');
    if (!bundledContent) return;
    bundledContent.replaceWith(variantPickerDoc.cloneNode(true));
  }
  updateBuyButton(buyButtonDoc) {
    const buyButton = this.querySelector('form.add-to-cart-form');
    if (!buyButton) return;
    buyButton.replaceWith(buyButtonDoc.cloneNode(true));
  }
  loadProduct(handle) {
    const requestId = ++this.bundleProductRequestId;
    this.updateAddToCartButtonState(true);
    this.updateVariantPickerLoading(true);
    this.fetchProduct({ handle }).then((htmlDoc) => {
      if (requestId !== this.bundleProductRequestId) return;
      const mediaDoc = htmlDoc.querySelector('custom-main-template-slides');
      const priceDoc = htmlDoc.querySelector('.ctm-protemp_price');
      const variantPickerDoc = htmlDoc.querySelector('.custom-bundled__content');
      const buyButtonDoc = htmlDoc.querySelector('form.add-to-cart-form');
      this.updateMedia(mediaDoc);
      this.updatePrice(priceDoc);
      this.updatePid(variantPickerDoc);
      this.updateVariant(variantPickerDoc);
      this.updateBuyButton(buyButtonDoc);
      this.customFieldsData();
      this.initWebComponents();
    }).catch((error) => {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    }).finally(() => {
      if (requestId !== this.bundleProductRequestId) return;
      this.updateVariantPickerLoading(false);
    })
  }
  initLoadProduct({ handle, sku }) {
    const requestId = ++this.bundleProductRequestId;
    this.updateAddToCartButtonState(true);
    this.fetchProduct({ handle, sku }).then((htmlDoc) => {
      if (requestId !== this.bundleProductRequestId) return;
      const variantPickerDoc = htmlDoc.querySelector('.custom-bundled__content');
      const buyButtonDoc = htmlDoc.querySelector('form.add-to-cart-form');
      this.updatePid(variantPickerDoc);
      this.updateVariant(variantPickerDoc);
      this.updateBuyButton(buyButtonDoc);
      this.customFieldsData();
      this.initWebComponents();
    }); 
  }
}
customElements.define('custom-product-bundled', CustomProductBundled);

class CustomProductTemplateSlide extends HTMLElement {
  constructor() {
    super();
    this.mainSlideInstance = null;
    this.thumbSlideInstance = null;
    this.slideObserve = null;
    this.videos = this.querySelectorAll('.main-swiper__video');
    this.photoZoomIcon = this.querySelector('.main-swiper__photo-zoom');
  }
  connectedCallback() {
    if (this.classList.contains('active')) {
      this.createSwiper();
    }
    this.slideObserve = new MutationObserver(() => {
      this.classList.contains('active') ? this.createSwiper() : this.destroySwiper();
    });
    this.slideObserve.observe(this, { attributes: true, attributeFilter: ['class'] });
    this.observeVideo();
    this.initImageZoom();
    // this.setupMouseMask();
  }
  disconnectedCallback() {
    this.destroySwiper();
    this.slideObserve?.disconnect();
    this.lightbox?.destroy();
    this.destroyMouseMask?.();
  }
  createSwiper() {
    const mainSlide = this.querySelector('.main-swiper');
    const thumbSlide = this.querySelector('.thumb-swiper');
    this.thumbSlideInstance = new window.FoxTheme.Carousel(thumbSlide, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      grabCursor: true,
      freeMode: true,
      watchSlidesProgress: true,
      breakpoints: {
        768: {
          direction: 'vertical',
          spaceBetween: 12,
        }
      }
    });
    this.thumbSlideInstance.init();
    this.mainSlideInstance = new window.FoxTheme.Carousel(mainSlide, {
      grabCursor: true,
      spaceBetween: 8,
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbSlideInstance.slider
      },
    }, [FoxTheme.Swiper.Thumbs]);
    this.mainSlideInstance.init();
  }
  destroySwiper() {
    this.mainSlideInstance?.slider.destroy();
    this.mainSlideInstance = null;
    this.thumbSlideInstance?.slider.destroy();
    this.thumbSlideInstance = null;
  }
  slideTo(index) {
    const slideIndex = Number(index) || 0;
    this.mainSlideInstance?.slider?.slideTo(slideIndex, 0);
  }
  getCurrentSlideIndex() {
    return this.mainSlideInstance?.slider?.activeIndex || 0;
  }
  observeVideo() {
    const reduceMotion = CustomProductAccessibility.prefersReducedMotion();
    for (const video of this.videos) {
      new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (reduceMotion) {
            video.pause();
            return;
          }
          entry.isIntersecting ? video.play() : video.pause();
        });
      }, { threshold: [0.3], delay: 0 }).observe(video);
    }
  }
  initImageZoom() {
    if (!this.photoZoomIcon || !window.FoxTheme?.PhotoSwipeLightbox || !window.FoxTheme?.PhotoSwipe) return;

    const getActiveMainSlide = () => {
      const slider = this.mainSlideInstance?.slider;
      return slider?.slides?.[slider.activeIndex] || null;
    };

    const pauseTemplateVideos = () => {
      this.querySelectorAll('.main-swiper video').forEach((video) => {
        video.pause();
      });
    };

    const playActiveTemplateVideo = () => {
      if (CustomProductAccessibility.prefersReducedMotion()) return;
      const video = getActiveMainSlide()?.querySelector('video');
      video?.play().catch(() => {});
    };

    const pauseZoomVideos = () => {
      this.lightbox?.pswp?.element?.querySelectorAll('video').forEach((video) => {
        video.pause();
      });
    };

    const playCurrentZoomVideo = () => {
      const currentSlide = this.lightbox?.pswp?.currSlide;
      if (currentSlide?.data?.mediaType !== 'video') return;

      const video = currentSlide.container?.querySelector('video');
      if (!video) return;

      video.muted = true;
      video.playsInline = true;
      video.play().catch(() => {});
    };

    const syncZoomVideo = () => {
      pauseZoomVideos();
      requestAnimationFrame(playCurrentZoomVideo);
    };

    const dataSource = [...this.querySelectorAll('.main-swiper .swiper-slide:not(.swiper-slide-duplicate)')]
      .map((media) => {
        const { mediaIndex, mediaType, src, pswpWidth, pswpHeight } = media.dataset;
        const source = {
          id: mediaIndex,
          mediaType
        };

        if (mediaType === 'video') {
          const videoClone = media.querySelector('video')?.cloneNode(true);
          if (!videoClone) return null;

          videoClone.autoplay = !CustomProductAccessibility.prefersReducedMotion();
          videoClone.muted = true;
          videoClone.playsInline = true;

          return {
            ...source,
            html: `<div class="pswp__item--video">${videoClone.outerHTML}</div>`
          };
        }

        if (!src || !pswpWidth || !pswpHeight) return null;

        return {
          ...source,
          src,
          width: pswpWidth,
          height: pswpHeight
        };
      })
      .filter(Boolean);
    if (!dataSource.length) return;

    this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
      dataSource,
      pswpModule: window.FoxTheme.PhotoSwipe,
      bgOpacity: 1,
      arrowPrev: false,
      arrowNext: false,
      zoom: false,
      close: false,
      counter: false,
      preloader: false
    });

    this.lightbox.addFilter('thumbEl', (thumbEl) => {
      return getActiveMainSlide()?.querySelector('img') || thumbEl;
    });

    this.lightbox.addFilter('placeholderSrc', (placeholderSrc) => {
      return getActiveMainSlide()?.querySelector('img')?.src || placeholderSrc;
    });

    this.lightbox.on('change', () => {
      pauseTemplateVideos();
      this.slideTo(this.lightbox?.pswp?.currIndex || 0);
      syncZoomVideo();
    });

    this.lightbox.on('appendHeavyContent', () => {
      syncZoomVideo();
    });
    this.lightbox.on('close', () => {
      pauseZoomVideos();
      playActiveTemplateVideo();
    });

    this.lightbox.on('pointerDown', (event) => {
      if (this.lightbox?.pswp?.currSlide?.data?.mediaType !== 'image') {
        event.preventDefault();
      }
    });

    this.lightbox.on('uiRegister', () => {
      const lightboxUI = this.lightbox?.pswp?.ui;
      if (!lightboxUI) return;

      if (dataSource.length > 1) {
        lightboxUI.registerElement({
          name: 'next',
          ariaLabel: 'Next slide',
          order: 3,
          isButton: true,
          html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
          onClick: () => {
            this.lightbox?.pswp?.next();
          }
        });
        lightboxUI.registerElement({
          name: 'prev',
          ariaLabel: 'Previous slide',
          order: 1,
          isButton: true,
          html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
          onClick: () => {
            this.lightbox?.pswp?.prev();
          }
        });
      }

      lightboxUI.registerElement({
        name: 'close-zoom',
        ariaLabel: 'Close zoom image',
        order: 2,
        isButton: true,
        html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
        onClick: () => {
          this.lightbox?.pswp?.close();
        }
      });
    });

    this.lightbox.init();

    this.photoZoomIcon.addEventListener('click', () => {
      pauseTemplateVideos();
      this.lightbox?.loadAndOpen(this.getCurrentSlideIndex());
    });
  }
  setupMouseMask() {
    if (this.dataset.mouseMask !== 'open') return;
    if (window.innerWidth <= 768) return;

    const config = {
      width: 120,
      height: 160,
      scale: 2
    };

    let mask = null;
    let scaleMask = null;
    let rafId = null;
    let activeContainer = null;
    let containerRect = null;

    const loadImage = (url) => new Promise((resolve) => {
      const img = new Image();
      img.src = url;
      img.onload = resolve;
      img.onerror = resolve;
    });

    const remove = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }

      mask?.remove();
      scaleMask?.remove();

      mask = null;
      scaleMask = null;
      activeContainer = null;
      containerRect = null;
    };

    const create = (container) => {
      const img = container.querySelector('img');
      if (!img) return;

      activeContainer = container;
      containerRect = container.getBoundingClientRect();

      mask = document.createElement('div');
      mask.className = 'main-swiper__mouse-mask';
      mask.style.width = `${config.width}px`;
      mask.style.height = `${config.height}px`;
      container.appendChild(mask);

      scaleMask = document.createElement('div');
      scaleMask.className = 'main-swiper__mouse-scale';
      scaleMask.style.width = `${config.width * config.scale}px`;
      scaleMask.style.height = `${config.height * config.scale}px`;

      const scaleImage = document.createElement('img');
      const currentSrc = img.src;
      const fullSrc = currentSrc.split('?')[0];

      scaleImage.src = currentSrc;
      scaleImage.style.width = `${containerRect.width * config.scale}px`;
      scaleImage.style.height = `${containerRect.height * config.scale}px`;
      scaleImage.style.maxWidth = 'none';

      scaleMask.appendChild(scaleImage);
      this.appendChild(scaleMask);

      loadImage(fullSrc).then(() => {
        if (scaleImage.isConnected) {
          scaleImage.src = fullSrc;
        }
      });
    };

    const update = (event) => {
      if (!mask || !scaleMask || !containerRect) return;

      const x = event.clientX - containerRect.left;
      const y = event.clientY - containerRect.top;
      const halfWidth = config.width / 2;
      const halfHeight = config.height / 2;
      const clampedX = Math.max(halfWidth, Math.min(x, containerRect.width - halfWidth));
      const clampedY = Math.max(halfHeight, Math.min(y, containerRect.height - halfHeight));

      mask.style.transform = `translate(${clampedX}px, ${clampedY}px) translate(-50%, -50%)`;

      const relativeX = (clampedX - halfWidth) / (containerRect.width - config.width);
      const relativeY = (clampedY - halfHeight) / (containerRect.height - config.height);
      const scaleImage = scaleMask.querySelector('img');
      if (!scaleImage) return;

      const maxMoveX = containerRect.width * config.scale - config.width * config.scale;
      const maxMoveY = containerRect.height * config.scale - config.height * config.scale;

      scaleImage.style.transform = `translate(${-relativeX * maxMoveX}px, ${-relativeY * maxMoveY}px)`;
    };

    const onMove = FoxTheme.utils.throttle((event) => {
      if (!activeContainer) return;
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => update(event));
    });

    this.querySelectorAll('.main-swiper .swiper-slide').forEach((container) => {
      container.addEventListener('mouseenter', (event) => {
        remove();
        create(container);
        update(event);
      });

      container.addEventListener('mousemove', onMove);
      container.addEventListener('mouseleave', remove);
    });

    this.destroyMouseMask = remove;
  }
}
customElements.define('custom-main-template-slide', CustomProductTemplateSlide);

class CustomProductTemplateSlides extends HTMLElement {
  constructor() {
    super();
    this.noActive = 'custom-main-template-slide:not(.active) .main-swiper .swiper-slide[data-index="0"]';
    this.activeImage = 'custom-main-template-slide.active .main-swiper .swiper-slide[data-index="0"] img';
  }
  connectedCallback() {
    this.initFetchFirstImage();
  }
  loadSlideImgs(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = url;
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  }
  fetchImages(urls) {
    const fetchImages = urls.map((url) => this.loadSlideImgs(url));
    return Promise.all(fetchImages);
  }
  initFetchThumbImgs() {
    const slides = this.querySelectorAll('custom-main-template-slide:not(.active)');
    for (const slide of slides) {
      const thumbs = slide.querySelectorAll('.thumb-swiper .swiper-slide:nth-of-type(-n+4)');
      for (const thumb of thumbs) {
        const img = thumb.querySelector('img');
        const url = img.src;
        const minUrl = url.replace('width=120', 'width=12');
        thumb.style.backgroundImage = `url(${minUrl})`;
        this.loadSlideImgs(minUrl).then(() => {
          thumb.style.backgroundImage = `url(${minUrl})`;
        })
      }
    }
  }
  loadFirstImage(pcUrls, mobileUrls) {
    setTimeout(() => {
      if (window.innerWidth > 768) {
        this.fetchImages(pcUrls);
      } else {
        this.fetchImages(mobileUrls);
      }
      this.initFetchThumbImgs();
    }, 8000);
  }
  initFetchFirstImage() {
    const firstImages = this.querySelectorAll(this.noActive);
    const mobileUrls = [];
    const pcUrls = [];
    for (const firstImage of firstImages) {
      const img = firstImage.querySelector('img');
      const srcset = img.srcset;
      const srcsetArr = srcset.split(/[, ]+/);
      const mobileUrl = srcsetArr.find(item => item.includes('width=560'));
      const pcUrl = srcsetArr.find(item => item.includes('width=800'));
      mobileUrls.push(mobileUrl);
      pcUrls.push(pcUrl);
    }
    const firstActiveImages = this.querySelector(this.activeImage);
    if (firstActiveImages) {
      const isImageLoaded = firstActiveImages.complete && firstActiveImages.naturalWidth > 0;
      if (isImageLoaded) {
        this.loadFirstImage(pcUrls, mobileUrls);
        return;
      }
      firstActiveImages.addEventListener('load', () => {
        this.loadFirstImage(pcUrls, mobileUrls);
      });
    }
  }
}
customElements.define('custom-main-template-slides', CustomProductTemplateSlides);

class CustomProductQuestionList extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.currentTrigger = null;
    this._parent = this.closest('.custom-product-template');
    this.url = window.CUSTOM_PRODUCT_COLLAPSE_DATA_URL;
    this.lists = []
  }
  normalizeText(text = '') {
    return text
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[\n\r]/g, ' ')
      .replace(/[：]/g, ':')
      .replace(/[’]/g, "'")
      .replace(/[“”]/g, '"');
  }
  loadQuestion() {
    if (!this.url) return;
    fetch(this.url).then(response => {
      return response.json();
    }).then(data => {
      this.lists = data.map(item => ({
        ...item,
        text: this.normalizeText(item.text)
      }));
    });
  }
  findMatchedItem(key) {
    if (!this.lists) return null;
    return this.lists.find(item => {
      return key === item.text;
    });
  }
  renderHeader(title) {
    const headerEl = this.shadowRoot.querySelector('#main-title');
    if (title) headerEl.textContent = title;
  }
  renderAccordion(items, mode) {
    const container = this.shadowRoot.querySelector('.accordion');
    container.innerHTML = '';

    const arrowSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M15 7.5L10 12.5L5 7.5" stroke="#666" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    items.forEach((item, index) => {
      const itemEl = document.createElement('div');
      itemEl.className = 'accordion-item';
      const showTitle = mode == 'collapse';
      const contentId = `product-info-panel-${index}`;
      const isOpen = index === 0;
      itemEl.innerHTML = `
        ${showTitle ? `
          <button class="accordion-title" type="button" aria-expanded="${isOpen}" aria-controls="${contentId}">
            <span>${item.title}</span>
            <span class="arrow" aria-hidden="true">${arrowSvg}</span>
          </button>
        ` : ''}
        <div class="accordion-content" id="${contentId}">${item.content}</div>
      `;
      const titleEl = itemEl.querySelector('.accordion-title');
      if (showTitle) {
        itemEl.classList.add('has-title');
        if (isOpen) itemEl.classList.add('open');
        titleEl.addEventListener('click', () => {
          const nextOpen = !itemEl.classList.contains('open');
          itemEl.classList.toggle('open', nextOpen);
          CustomProductAccessibility.setExpanded(titleEl, nextOpen);
        });
      } else {
        itemEl.classList.add('open');
      }
      container.appendChild(itemEl);
    });
  }
  showPopup(item) {
    this.renderHeader(item.title);
    this.renderAccordion(item.children || [], item.mode);

    const popup = this.shadowRoot.querySelector('.popup');
    const overlay = this.shadowRoot.querySelector('.overlay');

    overlay.classList.add('active');
    popup.classList.add('active');
    CustomProductAccessibility.setFocusable(popup, true);
    requestAnimationFrame(() => {
      this.shadowRoot.querySelector('.close-btn')?.focus();
    });
  }
  hidePopup() {
    const popup = this.shadowRoot.querySelector('.popup');
    const overlay = this.shadowRoot.querySelector('.overlay');

    popup.classList.remove('active');
    overlay.classList.remove('active');
    CustomProductAccessibility.setFocusable(popup, false);

    if (this.currentTrigger) {
      this.currentTrigger.focus();
      this.currentTrigger = null;
    }
  }
  render() {
    const template = document.getElementById('custom-product-collapse-popup-template');
    if (!template) return;
    const content = template.content.cloneNode(true);
    this.shadowRoot.innerHTML = '';
    this.shadowRoot.appendChild(content);

    const closeBtn = this.shadowRoot.querySelector('.close-btn');
    const overlay = this.shadowRoot.querySelector('.overlay');
    closeBtn.onclick = () => this.hidePopup();
    overlay.onclick = () => this.hidePopup();
    CustomProductAccessibility.setFocusable(this.shadowRoot.querySelector('.popup'), false);
    this.shadowRoot.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.hidePopup();
      }
    });
  }
  connectedCallback() {
    this.loadQuestion();
    this.render();
    this._parent.addEventListener('click', (e) => {
      const target = e.target.closest('.legend-label_question');
      if (!target) return;
      const key = this.normalizeText(target.dataset.key);
      if (!key) return;
      const matchedItem = this.findMatchedItem(key);
      if (!matchedItem) return;
      this.currentTrigger = target;
      this.showPopup(matchedItem);
    })
  }
}
customElements.define('custom-product-question-list', CustomProductQuestionList);

class CustomVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.section_id = this.dataset.sectionId;
    this.url = this.dataset.productUrl;
  }
  get defaultOptions() {
    return Array.from(this.querySelectorAll('fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
  connectedCallback() {
    this.addEventListener('change', (event) => {
      this.variantChanged(event);
    });
  }
  disconnectedCallback() {}
  variantChanged(event) {
    this.dispatchEvent(
      new CustomEvent("variant-change", {
        detail: {
          requestUrl: this.buildRequestUrlWithParams(),
          target: event.target
        },
        bubbles: true,
        composed: true
      })
    );
  }
  buildRequestUrlWithParams() {
    const params = [];
    // params.push(`section_id=${this.section_id}`);
    params.push(`view=bundled`);
    if (this.defaultOptions.length) {
      params.push(`option_values=${this.defaultOptions.join(',')}`);
    }
    return `${this.url}?${params.join('&')}`;
  }
}
customElements.define('custom-variant-picker', CustomVariantPicker);

class CustomFieldText extends HTMLElement {
  constructor() {
    super();
    this.wrapper = this.closest('.custom-fields');
    this.required = this.dataset.required || false;
    this.input = this.querySelector('input');
    this.field = this.closest('div');
    // this._parent = this.closest('fieldset').dataset.key;
    this.error = null;
  }
  get value() {
    // return [{ key: this._parent, value: this.input.value }]
    return this.input.value;
  }
  createError(text) {
    let timer;
    clearTimeout(timer);
    this.error = this.field.querySelector('.variant-error-tip');
    this.error && this.error.remove();
    this.error = document.createElement('div');
    this.error.className = 'variant-error-tip';
    this.field.appendChild(this.error);
    CustomProductAccessibility.setFieldError(this.field, this.input, this.error, text);
  }
  validateRule() {
    const value = this.input.value;
    if (this.required && !value.trim()) {
      const message = 'Please fill in field';
      this.createError(message);
      return {
        ok: false,
        value,
        message
      }
    } else {
      CustomProductAccessibility.clearFieldError(this.input, this.error);
      return {
        ok: true,
        value
      }
    }
  }
  connectedCallback() {
    this.input.addEventListener('input', (event) => {
      this.dispatchEvent(new CustomEvent('update-field-input', { bubbles: true }));
    });
  }
}
customElements.define('custom-field-text', CustomFieldText);

class CustomFieldDate extends HTMLElement {
  constructor() {
    super();
    this.required = this.dataset.required || false;
    this.displayFormat = 'DD/MM/YYYY';
    this.startOffset = this.parseInteger(this.dataset.startOffset, 0);
    this.maxSelectableUnits = this.parseInteger(this.dataset.maxSelectableUnits, null);
    this.disabledWeekdays = this.parseArray(this.dataset.disabledWeekdays)
      .map((day) => Number(day))
      .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);
    this.disabledDates = new Set(this.parseArray(this.dataset.disabledDates));
    this.input = this.querySelector('input');
    this.field = this.closest('div');
    this.error = null;
    this.picker = null;
    this.pickerAccessibility = null;
    const identifier = CustomProductAccessibility.ensureUniqueId(this.input, 'custom-field-date');
    this.calendarId = `${identifier}-calendar`;
  }
  get value() {
    return this.input.value;
  }
  parseInteger(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback;
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  parseArray(value) {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }
  flatpickrDateFormat() {
    const tokenMap = {
      YYYY: 'Y',
      YY: 'y',
      MM: 'm',
      DD: 'd'
    };
    return this.displayFormat.replace(/YYYY|YY|MM|DD/g, (token) => tokenMap[token] || token);
  }
  today() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }
  addDays(date, days) {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
  }
  formatDate(date) {
    const year = `${date.getFullYear()}`;
    const tokenMap = {
      YYYY: year,
      YY: year.slice(-2),
      MM: `${date.getMonth() + 1}`.padStart(2, '0'),
      DD: `${date.getDate()}`.padStart(2, '0')
    };
    return this.displayFormat.replace(/YYYY|YY|MM|DD/g, (token) => tokenMap[token] || token);
  }
  isDisabledDate(date) {
    return this.disabledWeekdays.includes(date.getDay()) || this.disabledDates.has(this.formatDate(date));
  }
  createError(text) {
    this.error?.remove();
    this.error = document.createElement('div');
    this.error.className = 'variant-error-tip';
    this.field.appendChild(this.error);
    CustomProductAccessibility.setFieldError(this.field, this.input, this.error, text);
  }
  validateRule() {
    const value = this.input.value;
    if (this.required && !value.trim()) {
      const message = 'Please select a date';
      this.createError(message);
      return {
        ok: false,
        value,
        message
      };
    }
    CustomProductAccessibility.clearFieldError(this.input, this.error);
    this.error = null;
    return {
      ok: true,
      value
    };
  }
  updateValue() {
    this.validateRule();
    this.dispatchEvent(new CustomEvent('update-field-input', { bubbles: true }));
  }

  connectedCallback() {
    const minDate = this.addDays(this.today(), this.startOffset);
    const afterTime = this.startOffset + this.maxSelectableUnits - 1;
    const maxDate = this.addDays(this.today(), afterTime);
    const dateRange = {};
    if (this.maxSelectableUnits > 0) {
      dateRange.minDate = minDate;
      dateRange.maxDate = maxDate;
    }
    if (window.flatpickr) {
      this.picker = window.flatpickr(this.input, {
        dateFormat: this.flatpickrDateFormat(),
        ...dateRange,
        disableMobile: true,
        disable: [
          (date) => this.isDisabledDate(date)
        ],
        animate: !CustomProductAccessibility.prefersReducedMotion(),
        onReady: (_selectedDates, _dateString, picker) => {
          this.pickerAccessibility = CustomProductAccessibility.setupDatePicker(this.input, picker, this.calendarId);
        },
        onMonthChange: () => this.pickerAccessibility?.syncNavigation(),
        onYearChange: () => this.pickerAccessibility?.syncNavigation(),
        onOpen: () => CustomProductAccessibility.setExpanded(this.input, true),
        onClose: () => CustomProductAccessibility.setExpanded(this.input, false),
        onChange: () => this.updateValue()
      });
    }
  }
  disconnectedCallback() {
    CustomProductAccessibility.setExpanded(this.input, false);
    this.pickerAccessibility?.destroy();
    this.picker?.destroy();
    this.pickerAccessibility = null;
    this.picker = null;
  }
}
customElements.define('custom-field-date', CustomFieldDate);

class CustomFieldNumber extends HTMLElement {
  constructor() {
    super();
    this.isComposing = false;
    this.min = this.getAttribute('min') || 0;
    this.max = this.getAttribute('max');
    this.required = this.dataset.required || false;
    // this._parent = this.closest('fieldset').dataset.key;
    this.input = this.querySelector('input');
    this.field = this.closest('div');
    this.error = null;
  }
  get value() {
    // return [{ key: this._parent, value: this.input.value }];
    return this.input.value;
  }
  set value(value) {
    this.input.value = value;
  }
  createError(text) {
    let timer;
    clearTimeout(timer);
    this.error = this.field.querySelector('.variant-error-tip');
    this.error && this.error.remove();
    this.error = document.createElement('div');
    this.error.className = 'variant-error-tip';
    this.field.appendChild(this.error);
    CustomProductAccessibility.setFieldError(this.field, this.input, this.error, text);
  }
  formatInput() {
    const { value } = this.validateRule();
    this.value = value;
    this.dispatchEvent(new CustomEvent('update-field-input', {
      bubbles: true
    }));
  }
  normalizeInteger(value) {
    return value.replace(/[\uFF10-\uFF19]/g, s =>
      String.fromCharCode(s.charCodeAt(0) - 0xFEE0)
    ).replace(/\D/g, '');
  }
  validateRule() {
    const value = this.normalizeInteger(this.input.value);
    let num = parseInt(value, 10);
    if (this.required && isNaN(num)) {
      const message = 'Please fill in field';
      this.createError(message);
      return {
        ok: false,
        value: '',
        message
      }
    } else if (num > 0 && (num < this.min || num > this.max)) {
      const message = `must be between ${this.min} and ${this.max}.`;
      this.createError(message);
      return {
        ok: false,
        value: num,
        message
      }
    } else {
      CustomProductAccessibility.clearFieldError(this.input, this.error);
      return {
        ok: true,
        value
      }
    }
  }
  connectedCallback() {
    this.input.addEventListener('compositionstart', () => {
      this.isComposing = true;
    });
    this.input.addEventListener('compositionend', (e) => {
      this.isComposing = false;
      this.formatInput();
    });
    this.input.addEventListener('input', (e) => {
      if (this.isComposing) return;
      this.formatInput();
    });
  }
}
customElements.define('custom-field-number', CustomFieldNumber);

class CustomFieldSize extends HTMLElement {
  constructor() {
    super();
    this.key = this.closest('fieldset').dataset.key;
    this.allowFraction = this.dataset.allowFraction === 'true';
    this.required = this.dataset.required || true;
    this.sizeInputs = this.querySelectorAll('.custom-size-field-input input');
    this.sizeWidth = this.querySelector('.custom-size-field-input input[data-type="width"]');
    this.sizeLength = this.querySelector('.custom-size-field-input input[data-type="length"]');
    this.widthFractionSelect = this.querySelector('custom-field-select[data-select-type="width"]');
    this.lengthFractionSelect = this.querySelector('custom-field-select[data-select-type="length"]');
    this.unitBtns = this.querySelectorAll('.custom-unit-btn');
    this.unitLabels = this.querySelectorAll('.custom-unit-label');
    this.enableCm = this.dataset.enableCm === 'true';
    this.sizeUnit = this.enableCm ? 'cm' : 'inches';
  }
  get sizeValidationRule() {
    return this.closest('.custom-product-template').fields?.find((field) => field.optionKey === this.key);
  }
  get widthInputValue() {
    return this.sizeWidth.value;
  }
  get lengthInputValue() {
    return this.sizeLength.value;
  }
  set widthInputValue(value) {
    this.sizeWidth.value = value;
  }
  set lengthInputValue(value) {
    this.sizeLength.value = value;
  }
  get widthFractionSelectValue() {
    return this.widthFractionSelect ? this.widthFractionSelect.value[0].value : '';
  }
  get lengthFractionSelectValue() {
    return this.lengthFractionSelect ? this.lengthFractionSelect.value[0].value : '';
  }
  set widthFractionSelectValue(value = '-/-') {
    this.setFractionSelectValue(this.widthFractionSelect, value);
    this.widthFractionSelect.value = value;
  }
  set lengthFractionSelectValue(value = '-/-') {
    this.setFractionSelectValue(this.lengthFractionSelect, value);
    this.lengthFractionSelect.value = value;
  }
  get widthInchesValue() {
    return this.sizeUnit === 'inches' ? this.widthInputValue : this.cmToInches(this.widthInputValue, 1).value;
  }
  get lengthInchesValue() {
    return this.sizeUnit === 'inches' ? this.lengthInputValue : this.cmToInches(this.lengthInputValue, 1).value;
  }
  normalizeInteger(value = '') {
    const normalized = String(value)
      .replace(/[０-９]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
      .replace(/[eE+-]/g, '');
    if (this.sizeUnit === 'cm') {
      const cleaned = normalized.replace(/[^\d.]/g, '');
      const match = cleaned.match(/^\d+(?:\.\d)?/);
      return match ? match[0] : '';
    }
    return normalized.replace(/\D/g, '');
  }
  shouldRewriteInput(value = '') {
    const current = String(value ?? '');
    if (this.sizeUnit === 'cm') {
      return !/^\d+$|^\d+\.\d$/.test(current);
    }
    return true;
  }
  toComparableNumber(value = '') {
    const normalized = this.normalizeInteger(value);
    if (normalized === '') return NaN;
    return this.sizeUnit === 'cm'
      ? Number.parseFloat(normalized)
      : Number.parseInt(normalized, 10);
  }
  fractionToDecimal(fraction = '-/-') {
    if (!fraction || ['-/-', '0/0'].includes(fraction)) return 0;
    const [numerator, denominator] = fraction.split('/').map(Number);
    if (!numerator || !denominator) return 0;
    return numerator / denominator;
  }
  formatEighthFraction(eighths = 0) {
    const fractionMap = {
      0: '0/0',
      1: '1/8',
      2: '1/4',
      3: '3/8',
      4: '1/2',
      5: '5/8',
      6: '3/4',
      7: '7/8'
    };
    return fractionMap[eighths] || '-/-';
  }
  setFractionSelectValue(select, value = '-/-') {
    if (!select) return;
    select.querySelector('.tfs__option--selected')?.classList.remove('tfs__option--selected');
    const option = select.querySelector(`.tfs__option[data-label="${value}"]`) || select.querySelector('.tfs__option[data-label="-/-"]');
    option?.classList.add('tfs__option--selected');
  }
  inchesToCm(value, fraction = '-/-', step = 0) {
    if (!value || isNaN(value)) {
      return {
        value: ''
      };
    }
    const inches = Number(value) + this.fractionToDecimal(fraction);
    let _value = Math.round(inches * 2.54 * 10) / 10;
    if (step === 0) {
      _value = Math.round(inches * 2.54);
    }
    return { value: _value };
  }
  cmToInches(value, type = 0) {
    if (!value || isNaN(value)) {
      return {
        value: '',
        fraction: '-/-'
      };
    }
    if (type === 0 || !this.allowFraction) {
      return {
        value: Math.round(Number(value) / 2.54),
        fraction: '-/-'
      };
    }
    const totalEighths = Math.round((Number(value) / 2.54) * 8);
    const integer = Math.floor(totalEighths / 8);
    const fraction = this.formatEighthFraction(totalEighths % 8);
    return {
      value: integer,
      fraction
    };
  }
  cmToInchesDecimal(value, precision = 4) {
    if (!value || isNaN(value)) return '';
    return Number((Number(value) / 2.54).toFixed(precision));
  }
  getFractionMaxOffset() {
    return this.allowFraction ? 7 / 8 : 0;
  }
  getMaxComparableValue(value) {
    const numericValue = Number(value);
    if (isNaN(numericValue)) return numericValue;
    return numericValue + this.getFractionMaxOffset();
  }
  formatInchesRuleValue(value) {
    return Math.floor(Number(value));
  }
  toComparableInches(value = '') {
    const normalized = this.toComparableNumber(value);
    if (isNaN(normalized)) return NaN;
    if (this.sizeUnit !== 'cm') return normalized;
    return this.cmToInchesDecimal(normalized, 4);
  }
  formatRuleValue(value) {
    const numericValue = Number(value);
    if (isNaN(numericValue)) return value;
    return this.sizeUnit === 'cm'
      ? this.inchesToCm(numericValue, '-/-', 1).value
      : this.formatInchesRuleValue(numericValue);
  }
  switchUnitValidate() {
    const errorTipNumbers = this.querySelectorAll('.variant-error-tip span[data-number]');
    for (const num of errorTipNumbers) {
      num.textContent = this.formatRuleValue(num.dataset.number);
    }
  }
  switchUnit(newUnit) {
    const unit = newUnit.dataset.unit;
    if (newUnit.classList.contains('custom-unit-btn--active')) return;
    this.sizeUnit = unit;
    this.querySelectorAll('.custom-size-field-fraction').forEach(el => {
      el.style.display = unit === 'cm' ? 'none' : '';
    });
    this.unitLabels.forEach(label => { label.textContent = unit; });
    this.unitBtns.forEach((button) => {
      const isActive = button === newUnit;
      button.classList.toggle('custom-unit-btn--active', isActive);
      CustomProductAccessibility.setPressed(button, isActive);
    });
    if (unit === 'cm') {
      const width = this.inchesToCm(this.widthInputValue, this.widthFractionSelectValue, 1);
      const length = this.inchesToCm(this.lengthInputValue, this.lengthFractionSelectValue, 1);
      this.widthInputValue = width.value;
      this.lengthInputValue = length.value;
      if (this.allowFraction) {
        const widthWrapper = this.widthFractionSelect.closest('.custom-size-field');
        const lengthWrapper = this.lengthFractionSelect.closest('.custom-size-field');
        this.removeFractionError(widthWrapper);
        this.removeFractionError(lengthWrapper);
      }
    } else {
      const width = this.cmToInches(this.widthInputValue, 1);
      const length = this.cmToInches(this.lengthInputValue, 1);
      this.widthInputValue = width.value;
      this.lengthInputValue = length.value;
    }
    this.switchUnitValidate();
    this.dispatchEvent(new CustomEvent('update-field-size', { bubbles: true }));
  }
  createError(text, field) {
    let error = field.querySelector('.variant-error-tip');
    error && error.remove();
    error = document.createElement('div');
    error.className = 'variant-error-tip';
    field.appendChild(error);
    CustomProductAccessibility.setFieldError(field, field.querySelector('input'), error, text);
  }
  removeError(field) {
    const error = field.querySelector('.variant-error-tip');
    CustomProductAccessibility.clearFieldError(field.querySelector('input'), error);
  }
  createFractionError(text, field) {
    let error = field.querySelector('.variant-fraction-error-tip');
    error && error.remove();
    error = document.createElement('div');
    error.className = 'variant-fraction-error-tip';
    field.appendChild(error);
    CustomProductAccessibility.setFieldError(field, field.querySelector('custom-field-select input'), error, text);
  }
  removeFractionError(field) {
    const error = field.querySelector('.variant-fraction-error-tip');
    CustomProductAccessibility.clearFieldError(field.querySelector('custom-field-select input'), error);
  }
  connectedCallback() {
    for (const sizeInput of this.sizeInputs) {
      let isComposing = false;
      sizeInput.addEventListener('compositionstart', () => {
        isComposing = true;
      });
      sizeInput.addEventListener('compositionend', (e) => {
        isComposing = false;
        this.formatInput(e.target.dataset.type);
      });
      sizeInput.addEventListener('input', (e) => {
        if (isComposing) return;
        this.formatInput(e.target.dataset.type);
      });
      sizeInput.addEventListener('wheel', (e) => {
        e.target.blur();
      });
    }
    this.addEventListener('update-field-size-select', ({ detail }) => {
      this.fractionValidateRule(detail?.type);
      this.dispatchEvent(new CustomEvent('update-field-size', { bubbles: true }));
    });
    if (this.enableCm) {
      for (const unitBtn of this.unitBtns) {
        unitBtn.addEventListener('click', () => this.switchUnit(unitBtn));
      }
    }
  }
  formatInput(type) {
    const { tps_width_value, tps_length_value } = this.validateRule(type);
    if (this.shouldRewriteInput(tps_width_value)) {
      this.widthInputValue = this.toComparableNumber(tps_width_value);
    }
    if (this.shouldRewriteInput(tps_length_value)) {
      this.lengthInputValue = this.toComparableNumber(tps_length_value);
    }
    if (this.sizeUnit === 'cm' && this.allowFraction) {
      const width = this.cmToInches(this.widthInputValue, 1);
      const length = this.cmToInches(this.lengthInputValue, 1);
      this.widthFractionSelectValue = width.fraction;
      this.lengthFractionSelectValue = length.fraction;
    }
    this.dispatchEvent(new CustomEvent('update-field-size', { bubbles: true }));
  }
  validateRule(type) {
    const minWidth = Number(this.sizeWidth.dataset.min);
    const maxWidth = Number(this.sizeWidth.dataset.max);
    const minLength = Number(this.sizeLength.dataset.min);
    const maxLength = Number(this.sizeLength.dataset.max);
    const widthValue = this.sizeWidth.value;
    const lengthValue = this.sizeLength.value;

    const _width = this.toComparableInches(widthValue);
    const _length = this.toComparableInches(lengthValue);
    
    const widthWrapper = this.sizeWidth.closest('.custom-size-field');
    const lengthWrapper = this.sizeLength.closest('.custom-size-field');
    const baseRules = {
      width: { min: minWidth, max: this.getMaxComparableValue(maxWidth), wrapper: widthWrapper },
      length: { min: minLength, max: this.getMaxComparableValue(maxLength), wrapper: lengthWrapper },
    };
    const value = type === "width" ? _width : _length;
    const { min, max, wrapper } = baseRules[type] ?? {};
    if (isNaN(value)) {
      if (this.required) {
        const message = 'Please fill in field';
        this.createError(message, wrapper);
        return {
          ok: false,
          message,
          tps_width_value: widthValue,
          tps_length_value: lengthValue
        }
      }
    } else if (value < min || value > max) {
      const message = `${type} must be between <span data-number="${min}">${this.formatRuleValue(min)}</span> and <span data-number="${max}">${this.formatRuleValue(max)}</span>`;
      this.createError(message, wrapper);
      return {
        ok: false,
        message,
        tps_width_value: widthValue,
        tps_length_value: lengthValue
      }
    } else {
      const { constraints = [] } = this.sizeValidationRule?.validationRules || {};
      const result = this.validate(constraints, _length, _width, type);
      if (!result.ok) {
        this.createError(result.message, wrapper);
        return {
          ok: false,
          message: result.message,
          tps_width_value: widthValue,
          tps_length_value: lengthValue
        }
      } else {
        if (result.type === 'all') {
          this.removeError(widthWrapper);
          this.removeError(lengthWrapper);
        } else {
          this.removeError(wrapper);
        }
        return {
          ok: true,
          tps_width_value: widthValue,
          tps_length_value: lengthValue
        }
      }
    }
  }
  fractionValidateRule(type = 'width') {
    if (!this.allowFraction || this.sizeUnit === 'cm') return { ok: true };
    const widthWrapper = this.widthFractionSelect.closest('.custom-size-field');
    const lengthWrapper = this.lengthFractionSelect.closest('.custom-size-field');
    const baseRules = {
      width: { wrapper: widthWrapper, value: this.widthFractionSelectValue },
      length: { wrapper: lengthWrapper, value: this.lengthFractionSelectValue },
    };
    const { wrapper, value } = baseRules[type];
    if (value === '-/-') {
      this.createFractionError('Please select a fraction.', wrapper);
      return {
        ok: false,
        message: 'Please select a fraction.'
      }
    } else {
      this.removeFractionError(wrapper);
      return {
        ok: true
      }
    }
  }
  validate(rules, length, width, changedField) {
    const values = { length, width };
    const hitRule = rules.some((rule) => {
      const { whenOk, thenOk } = this.returnValue(rule, values);
      return whenOk && thenOk
    });
    if (hitRule) {
      return { ok: true, type: 'all' };
    }
    for (const rule of rules) {
      const { when, then, id } = rule;
      const { whenOk, thenOk, whenDim, thenDim } = this.returnValue(rule, values);
      if (changedField === thenDim && whenOk && !thenOk) {
        return {
          ok: false,
          ruleId: id,
          message: this._buildMessage(then, changedField)
        };
      }
      if (changedField === whenDim && thenOk && !whenOk) {
        return {
          ok: false,
          ruleId: id,
          message: this._buildMessage(when, changedField)
        };
      }
    }
    return { ok: true };
  } 
  returnValue(rule, values) {
    const { when, then } = rule;
    const whenDim = when.dimension;
    const thenDim = then.dimension;
    const whenValue = values[whenDim];
    const thenValue = values[thenDim];
    const whenOk = this.checkCondition(when, whenValue);
    const thenOk = this.checkCondition(then, thenValue);
    return { whenOk, thenOk, thenDim, whenDim };
  }
  checkCondition(cond, val) {
    if (cond.comparator === "between") {
      const min = Number(cond.min);
      const max = this.getMaxComparableValue(cond.max);
      return val >= min && val <= max;
    } 
    if (cond.comparator === "equals") {
      const value = Number(cond.value);
      return val === value;
    }
    return false;
  }
  _buildMessage(rulePart, field) {
    if (rulePart.comparator === "between") {
      const min = Number(rulePart.min);
      const max = this.getMaxComparableValue(rulePart.max);
      return `${field} must be between <span data-number="${min}">${this.formatRuleValue(min)}</span> and <span data-number="${max}">${this.formatRuleValue(max)}</span>.`;
    }
    if (rulePart.comparator === "equals") {
      const value = Number(rulePart.value);
      return `${field} must be equal to <span data-number="${value}">${this.formatRuleValue(value)}</span>.`;
    }
    return `${field} does not meet the requirements.`;
  }
}
customElements.define('custom-field-size', CustomFieldSize);

class CustomFieldSelect extends HTMLElement {
  constructor() {
    super();
    this.required = this.dataset.required || false;
    this.allowMultiple = this.dataset.allowMultiple || false;
    this.selectType = this.dataset.selectType || 1;
    this.input = this.querySelector('input');
    this.optionsContainer = this.querySelector('.tfs__options');
    this.options = this.querySelectorAll('.tfs__options li');
    this.field = this.closest('div');
    this.error = null;
    this.activeIndex = Math.max(0, Array.from(this.options).findIndex(option => option.classList.contains('tfs__option--selected')));
  }
  get optionsHeight() {
    return this.optionsContainer.scrollHeight;
  }
  get enabledOptions() {
    return Array.from(this.options).filter(option => (
      option.getAttribute('aria-disabled') !== 'true' &&
      option.getAttribute('aria-hidden') !== 'true'
    ));
  }
  get value() {
    const checked = this.querySelectorAll('li.tfs__option--selected');
    return [...checked].map(({ dataset }) => ({ key: dataset.key, value: dataset.label }));
  }
  set value(value) {
    this.input.value = value;
  }
  showOptions() {
    this.optionsContainer.style.display = 'block';
    CustomProductAccessibility.setExpanded(this.input, true);
    setTimeout(() => {
      this.optionsContainer.style.height = `${this.optionsHeight}px`;
    });
  }
  hideOptions() {
    CustomProductAccessibility.setExpanded(this.input, false);
    CustomProductAccessibility.clearActiveDescendant(this.input);
    this.optionsContainer.style.height = '0';
    setTimeout(() => {
      this.optionsContainer.style.display = 'none';
    }, 300);
  }
  syncSelectedState() {
    CustomProductAccessibility.syncSelectedOptions(this.options, 'tfs__option--selected');
  }
  setActiveOption(option) {
    if (!option) return;
    this.options.forEach(item => item.classList.remove('tfs__option--active'));
    option.classList.add('tfs__option--active');
    CustomProductAccessibility.setActiveDescendant(this.input, option);
    this.activeIndex = this.enabledOptions.indexOf(option);
    option.scrollIntoView({ block: 'nearest' });
  }
  moveActiveOption(direction) {
    const options = this.enabledOptions;
    if (!options.length) return;
    this.showOptions();
    const nextIndex = (this.activeIndex + direction + options.length) % options.length;
    this.setActiveOption(options[nextIndex]);
  }
  selectOption(option) {
    if (!option || option.classList.contains('tfs__option--selected')) {
      this.hideOptions();
      return;
    }
    this.optionsContainer.querySelector('.tfs__option--selected')?.classList.remove('tfs__option--selected');
    option.classList.add('tfs__option--selected');
    this.syncSelectedState();
    this.value = this.value.map(({ value }) => value).join(',');
    this.validateRule();
    CustomProductAccessibility.clearFieldError(this.input, this.error);
    if (this.selectType == 1) {
      this.dispatchEvent(new CustomEvent('update-field-checked', {
        bubbles: true
      }));
    }
    if (['width', 'length'].includes(this.selectType))  {
      this.dispatchEvent(new CustomEvent('update-field-size-select', {
        detail: { type: this.selectType },
        bubbles: true
      }));
    }
    this.hideOptions();
  }
  createError(text) {
    let timer;
    clearTimeout(timer);
    this.error = this.field.querySelector('.variant-error-tip');
    this.error && this.error.remove();
    this.error = document.createElement('div');
    this.error.className = 'variant-error-tip';
    this.field.prepend(this.error);
    CustomProductAccessibility.setFieldError(this.field, this.input, this.error, text);
  }
  validateRule() {
    const value = this.input.value;
    const valid = Array.from(this.querySelectorAll('li.tfs__option--selected')).filter(
      li => li.getAttribute('aria-disabled') !== 'true' && li.getAttribute('aria-hidden') !== 'true'
    )
    if (this.required && (!value || valid.length <= 0)) {
      const message = 'Please select in field';
      this.createError(message);
      return {
        ok: false,
        value,
        message
      }
    }
    return {
      ok: true,
      value
    }
  }
  connectedCallback() {
    this.options.forEach((option, index) => {
      if (!option.id) {
        option.id = `${this.input.id || this.input.name}-option-${index}`;
      }
    });
    this.syncSelectedState();
    this.setActiveOption(this.optionsContainer.querySelector('.tfs__option--selected') || this.enabledOptions[0]);
    this.input.addEventListener('focus', () => {
      this.showOptions();
    });
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveActiveOption(1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveActiveOption(-1);
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const option = this.optionsContainer.querySelector('.tfs__option--active') || this.optionsContainer.querySelector('.tfs__option--selected');
        this.selectOption(option);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.hideOptions();
      }
    });
    this.input.addEventListener('blur', (event) => {
      if (event.relatedTarget && event.relatedTarget?.closest('custom-field-select') === this.input.closest('custom-field-select')) return;
      this.hideOptions();
    });
    for (const option of this.options) {
      option.addEventListener('click', () => {
        this.selectOption(option);
      });
      option.addEventListener('mousemove', () => {
        this.setActiveOption(option);
      });
    }
  }
}
customElements.define('custom-field-select', CustomFieldSelect)

class CustomFieldCheckbox extends HTMLElement {
  constructor() {
    super();
    this.required = this.dataset.required || false;
    this.allowMultiple = this.dataset.allowMultiple || false;
    this.minMultiple = this.dataset.minMultiple;
    this.maxMultiple = this.dataset.maxMultiple;
    this.field = this.closest('.custom-fields');
    this.error = null;
  }
  get value() {
    const checked = this.querySelectorAll('input:checked');
    return [...checked].map(({ value, dataset }) => ({ key: dataset.key, value }));
  }
  get checkedCount() {
    return Array.from(this.querySelectorAll('input:checked')).filter(
      input => input.getAttribute('aria-disabled') !== 'true' && input.getAttribute('aria-hidden') !== 'true'
    ).length;
  }
  createError(text, hide) {
    let timer;
    clearTimeout(timer);
    this.error = this.field.querySelector('.variant-error-tip');
    this.error && this.error.remove();
    this.error = document.createElement('div');
    this.error.className = 'variant-error-tip';
    this.field.prepend(this.error);
    const firstInput = this.querySelector('input');
    CustomProductAccessibility.setFieldError(this.field, firstInput, this.error, text);
    if (!hide) return;
    timer = setTimeout(() => {
      CustomProductAccessibility.clearFieldError(firstInput, this.error);
    }, 3000);
  }
  validateRule() {
    if (this.required && this.checkedCount === 0) {
      const message = 'Please select at least one option';
      this.createError(message);
      return {
        ok: false,
        message
      }
    }
    if (this.allowMultiple) {
      if (this.checkedCount < this.minMultiple) {
        const message = `Please select at least ${this.minMultiple} options`;
        this.createError(message);
        return {
          ok: false,
          message
        }
      }
    }
    return {
      ok: true
    }
  }
  checkedRule() {
    this.addEventListener('change', (event) => {
      if (this.required && this.allowMultiple) {
        if (this.checkedCount > this.maxMultiple) {
          event.target.checked = false;
          this.createError(`No more than ${this.maxMultiple} at most`, true);
          return;
        }
      }
      CustomProductAccessibility.clearFieldError(this.querySelector('input'), this.error);
      this.dispatchEvent(new CustomEvent('update-field-checked', { bubbles: true }));
    });
  }
}
class CustomFieldButton extends CustomFieldCheckbox {
  constructor() {
    super();
  }
  connectedCallback() {
    this.checkedRule();
  }
}
customElements.define('custom-field-button', CustomFieldButton);
class CustomFieldImage extends CustomFieldCheckbox {
  constructor() {
    super();
  }
  connectedCallback() {
    this.checkedRule();
  }
}
customElements.define('custom-field-image', CustomFieldImage);

class CustomFieldUpload extends HTMLElement {
  constructor() {
    super();
    this.required = this.dataset.required || false;
    this.input = this.querySelector('input');
    this.field = this.closest('.custom-fields');
    this.error = null;
  }
  createError(text) {
    this.error = this.field.querySelector('.variant-error-tip');
    this.error && this.error.remove();
    this.error = document.createElement('div');
    this.error.className = 'variant-error-tip';
    this.field.prepend(this.error);
    CustomProductAccessibility.setFieldError(this.field, this.input, this.error, text);
  }
  validateRule() {
    if (this.required && !this.input.files.length) {
      const message = 'Please upload one file';
      this.createError(message);
      return {
        ok: false,
        message
      }
    }
    return {
      ok: true
    }
  }
  connectedCallback() {
    this.input.addEventListener('change', (e) => {
      const files = e.target.files
      if (files && files.length > 0) {
        CustomProductAccessibility.clearFieldError(this.input, this.error);
      }
    });
  }
}
customElements.define('custom-field-upload', CustomFieldUpload);

class CustomFieldRadio extends HTMLElement {
  constructor() {
    super();
    this.field = this.closest('.custom-fields');
  }
  get value() {
    const checked = this.querySelectorAll('input:checked');
    return [...checked].map(({ value, dataset }) => ({ key: dataset.key, value }));
  }
  connectedCallback() {
    this.addEventListener('change', () => {
      this.dispatchEvent(new CustomEvent('update-field-checked', { bubbles: true }));
    });
  }
}
customElements.define('custom-field-radio', CustomFieldRadio);

class CustomQuantityInput extends HTMLElement {
  constructor() {
    super();
    this.min = this.getAttribute('min') || 1;
    this.max = this.getAttribute('max') || 999;
    this.input = this.querySelector('input');
    this.btns = this.querySelectorAll('.custom-quantity__icon');
    this.changeUnit = this.getAttribute('data-change-unit') || 0;
  }
  get value() {
    return this.input.value;
  }
  set value(value) {
    this.input.value = value;
    this.handleChangeUnit(value);
  }
  handleChangeUnit(value) {
    if (this.changeUnit != 1) return;
    const unitNode = this.querySelector('.custom-quantity__unit');
    if (!unitNode) return;
    const unit = unitNode.dataset.unit;
    unitNode.innerHTML = value > 1 ? `${unit}s` : unit;
  }
  formatInput(value) {
    let num = parseInt(value.replace(/\D+/g, ''), 10);
    if (isNaN(num)) {
      this.value = this.min;
    } else if (num < this.min) {
      this.value = this.min;
    } else if (num > this.max) {
      this.value = this.max;
    } else {
      this.value = num;
    }
  }
  connectedCallback() {
    this.input.addEventListener('input', (e) => {
      this.formatInput(e.target.value);
    });
    for (const btn of this.btns) {
      btn.addEventListener('click', () => {
        const name = btn.getAttribute('name');
        const value = parseInt(this.value);
        if (name === 'plus') {
          this.value = value >= this.max ? this.max : value + 1;
        } else if (name === 'minus') {
          this.value = value <= this.min ? this.min : value - 1;
        }
      })
    }
  }
  // changeQuantity() {
  //   document.dispatchEvent(new CustomEvent('quantity:update', {
  //     detail: {}
  //   }));
  //   // document.removeEventListener('quantity:update', this.updateCartContent.bind(this));
  // }
}
customElements.define('custom-quantity-input', CustomQuantityInput);

class CustomAddSwatchToCart extends HTMLElement {
  constructor() {
    super();
    this.id = Number(this.dataset.sku);
    if (!this.id) return;
    this.addToCartBtn = this.querySelector('.ctm-buyfabric_sku span');
    this.addToCartBtn.addEventListener('click', (event) => {
      this.addToCartBtn.classList.add('custom-loading-btn');
      this.addToCart(this.id);
    });
  }
  addToCart(id) {
    const formData = { items: [{ id, quantity: 1 }] };
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
      .then(response => {
        return response.json();
      })
      .then(data => {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: true }
        }));
        this.addToCartBtn.classList.remove('custom-loading-btn');
      })
      .finally(() => {
        this.addToCartBtn.classList.remove('custom-loading-btn');
      });
  }
}
customElements.define('custom-add-swatch-to-cart', CustomAddSwatchToCart);

class CustomMediaTab extends HTMLElement {
  constructor() {
    super();
    this.tabSelector = '.custom-media-tab__tab';
    this.onTabClick = this.onTabClick.bind(this);
  }

  connectedCallback() {
    this.initPhotoSwipe();
    this.querySelectorAll(this.tabSelector).forEach((button) => {
      button.removeEventListener('click', this.onTabClick);
      button.addEventListener('click', this.onTabClick);
    });
  }

  disconnectedCallback() {
    this.querySelectorAll(this.tabSelector).forEach((button) => {
      button.removeEventListener('click', this.onTabClick);
    });
    this.pauseZoomVideos();
    Object.values(this.lightboxByAction || {}).forEach((lightbox) => {
      lightbox?.destroy();
    });
    this.lightboxByAction = null;
    this.activeLightbox = null;
  }

  onTabClick(event) {
    const button = event.currentTarget;
    const action = button.dataset.action;

    this.openMedia(action);
  }

  initPhotoSwipe() {
    if (this.lightboxByAction || !window.FoxTheme?.PhotoSwipeLightbox || !window.FoxTheme?.PhotoSwipe) return;

    const parseMediaData = (value) => {
      if (!value) return [];
      try {
        const data = JSON.parse(value);
        return Array.isArray(data) ? data : [data];
      } catch (error) {
        return [];
      }
    };

    const getMediaUrl = (item) => {
      if (!item) return '';
      if (typeof item === 'string') return item;
      return item.url || item.src || item.preview_image?.src || item.image?.src || '';
    };

    const getMediaSize = (item) => {
      const width = Number(item?.width || item?.preview_image?.width || item?.image?.width) || 1200;
      const height = Number(item?.height || item?.preview_image?.height || item?.image?.height) || 1200;
      return { width, height };
    };

    const toSecureUrl = (url) => {
      if (!url) return '';
      if (url.startsWith('//')) return `${window.location.protocol}${url}`;
      return url.replace(/^http:\/\//, 'https://');
    };

    const getYoutubeId = (url) => {
      try {
        const videoUrl = new URL(url, window.location.origin);
        if (videoUrl.hostname.includes('youtu.be')) return videoUrl.pathname.replace('/', '').split('/')[0];
        if (videoUrl.pathname.includes('/shorts/')) return videoUrl.pathname.split('/shorts/')[1]?.split('/')[0];
        if (videoUrl.pathname.includes('/embed/')) return videoUrl.pathname.split('/embed/')[1]?.split('/')[0];
        return videoUrl.searchParams.get('v');
      } catch (error) {
        return '';
      }
    };

    const getVimeoId = (url) => {
      try {
        const videoUrl = new URL(url, window.location.origin);
        if (!videoUrl.hostname.includes('vimeo.com')) return '';
        return videoUrl.pathname.split('/').filter(Boolean).pop();
      } catch (error) {
        return '';
      }
    };

    const getEmbedUrl = (url) => {
      const secureUrl = toSecureUrl(url);
      if (!secureUrl) return '';

      if (secureUrl.includes('youtube.com') || secureUrl.includes('youtu.be')) {
        const videoId = getYoutubeId(secureUrl);
        return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0` : '';
      }

      if (secureUrl.includes('vimeo.com')) {
        const videoId = getVimeoId(secureUrl);
        return videoId ? `https://player.vimeo.com/video/${videoId}?autoplay=1` : '';
      }

      return secureUrl;
    };

    const escapeAttribute = (value) => {
      return String(value).replace(/"/g, '&quot;');
    };

    const createIframeHtml = (url) => {
      const embedUrl = getEmbedUrl(url);
      if (!embedUrl) return '';

      return `<div class="pswp__item--external_video custom-media-tab__pswp-video"><iframe src="${escapeAttribute(embedUrl)}" width="1000" height="563" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen loading="lazy" title="Embedded video"></iframe></div>`;
    };

    const mediaDataSourceByAction = {
      image: [],
      video: []
    };
    this.lightboxByAction = {};

    parseMediaData(this.dataset.imageTab).forEach((item) => {
      const src = toSecureUrl(getMediaUrl(item));
      if (!src) return;

      const { width, height } = getMediaSize(item);
      mediaDataSourceByAction.image.push({
        mediaType: 'image',
        src,
        width,
        height
      });
    });

    parseMediaData(this.dataset.videoTabs).forEach((item) => {
      const videoUrl = getMediaUrl(item);
      const html = createIframeHtml(videoUrl);
      if (!html) return;

      mediaDataSourceByAction.video.push({
        mediaType: 'external_video',
        html
      });
    });

    Object.entries(mediaDataSourceByAction).forEach(([action, dataSource]) => {
      if (!dataSource.length) return;

      const lightbox = new window.FoxTheme.PhotoSwipeLightbox({
        dataSource,
        pswpModule: window.FoxTheme.PhotoSwipe,
        bgOpacity: 1,
        arrowPrev: false,
        arrowNext: false,
        zoom: false,
        close: false,
        counter: false,
        preloader: false
      });

      lightbox.on('change', () => {
        this.pauseZoomVideos(false, lightbox);
      });

      lightbox.on('close', () => {
        this.pauseZoomVideos(true, lightbox);
      });

      lightbox.on('destroy', () => {
        this.pauseZoomVideos(true, lightbox);
      });

      lightbox.on('pointerDown', (event) => {
        if (lightbox?.pswp?.currSlide?.data?.mediaType !== 'image') {
          event.preventDefault();
        }
      });

      lightbox.on('uiRegister', () => {
        const lightboxUI = lightbox?.pswp?.ui;
        if (!lightboxUI) return;

        if (dataSource.length > 1) {
          lightboxUI.registerElement({
            name: 'next',
            ariaLabel: 'Next slide',
            order: 3,
            isButton: true,
            html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
            onClick: () => {
              lightbox?.pswp?.next();
            }
          });
          lightboxUI.registerElement({
            name: 'prev',
            ariaLabel: 'Previous slide',
            order: 1,
            isButton: true,
            html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
            onClick: () => {
              lightbox?.pswp?.prev();
            }
          });
        }

        lightboxUI.registerElement({
          name: 'close-zoom',
          ariaLabel: 'Close zoom image',
          order: 2,
          isButton: true,
          html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
          onClick: () => {
            lightbox?.pswp?.close();
          }
        });
      });

      lightbox.init();
      this.lightboxByAction[action] = lightbox;
    });
  }

  openMedia(action) {
    const lightbox = this.lightboxByAction?.[action];
    if (!lightbox) return;

    this.activeLightbox = lightbox;
    this.pauseZoomVideos();
    lightbox.loadAndOpen(0);
  }

  pauseZoomVideos(resetCurrent = true, targetLightbox = this.activeLightbox) {
    const currentSlide = targetLightbox?.pswp?.currSlide?.container;

    targetLightbox?.pswp?.element?.querySelectorAll('video').forEach((video) => {
      if (!resetCurrent && currentSlide?.contains(video)) return;
      video.pause();
      video.currentTime = 0;
    });

    targetLightbox?.pswp?.element?.querySelectorAll('iframe').forEach((iframe) => {
      if (!resetCurrent && currentSlide?.contains(iframe)) return;
      iframe.src = iframe.src;
    });
  }
}
customElements.define('custom-media-tab', CustomMediaTab);
